/**
 * OI Overlay Plugin for LightweightCharts v5
 *
 * Draws the OI-derived overlays behind/around the price series:
 * - OI distribution bars (raw call/put/total OI per strike)
 * - Projected GEX/DEX/VEX/CEX curves per session
 * - Exposure Profile (per-strike exposure at current spot, with an optional
 *   static T+1 next-session profile pinned at the right edge)
 * - Hedging Demand (per-strike combined dealer hedge flow bars from the
 *   /hedging-demand payload, with an optional time by strike heatmap
 *   sub-layer)
 * plus the session-level indicators (PDL/OR/IB, prior levels, pivots, OI levels).
 *
 * Real-time SSE (Server-Sent Events) updates supported for the exposure rows.
 */

import {
  atmSkew, atmCurvature, percentileK, densityModeK,
} from './essviSlice.js';

// Intensity rolloff for heatmap shading: intensity = sign(v) * pow(|v|/max, ROLLOFF).
const ROLLOFF = 0.7;

// Derived smile overlays are memoized per row candle: the eSSVI percentile
// and density-mode solves are cheap (<1ms) but run per render frame.
const SMILE_DERIVED_CACHE = new WeakMap();

// Smile-shape series values (SK/CU/FIT) per candle: ratio-independent
// slice geometry, computed once per row for the IV Skew Shape pane.
const SMILE_SHAPE_CACHE = new WeakMap();


// Fraction of the strike spacing a heatmap cell stack may fill; the rest
// stays empty as the inter-strike gutter so adjacent strikes never merge.
const STACK_FILL = 0.75;

// Fixed logical-pixel budget for the edge-pinned exposure profile histograms.
// These are pinned to the pane edges, so the budget must NOT scale with
// barSpacing or the columns drift toward center on zoom.
const PROFILE_MAX_WIDTH = 135;

// One calendar minute in years: the charm-hedge dt of the stored
// total_hedge_es (mirrors HEDGE_DT_CAL in calculations/greeks_heatmap.py;
// exposure-row charm runs on calendar time, do not swap to trading time).
const HEDGE_DT_CAL = 1 / (365.25 * 24 * 60);

/** RGB [0-255] triple -> HSL [h 0-360, s 0-1, l 0-1] */
function rgbToHsl([r, g, b]) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, s, l];
}

/** HSL [h 0-360, s 0-1, l 0-1] -> RGB [0-255] triple */
function hslToRgb([h, s, l]) {
  h = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rgb;
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return rgb.map(v => Math.round((v + m) * 255));
}

/**
 * Hot stop for the intensity spectrum: the palette's bright color with hue
 * rotated toward yellow (60deg) along the shorter arc, saturation pushed
 * vivid, lightness raised. This reads "hotter" within every palette family
 * without washing toward white, so hue carries the magnitude signal.
 * Memoized per bright color (computed per cell per frame otherwise).
 */
const _hotStopCache = new Map();
function hotStopFor(bright) {
  const key = bright.join(',');
  let hot = _hotStopCache.get(key);
  if (!hot) {
    const [h, s, l] = rgbToHsl(bright);
    let delta = 60 - h;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    // Tight in-family shift: enough hue movement to read "hotter", never so
    // light that opposing signs converge to a similar whitish pastel
    const shift = Math.max(-18, Math.min(18, delta));
    hot = hslToRgb([h + shift, Math.min(1, s * 1.15), Math.min(0.64, l + 0.06)]);
    _hotStopCache.set(key, hot);
  }
  return hot;
}

/**
 * Canvas renderer for the OI overlays.
 */
class GreeksHeatmapRenderer {
  constructor(series, chart, options = {}) {
    this.oiBarsData = options.oiBarsData || null;  // OI horizontal bars per session date
    this.oiCurvesData = options.oiCurvesData || null;  // OI projected curves per session date
    this.exposureRowsData = options.exposureRowsData || null;  // Exposure Profile: per-strike OI exposure rows per session date
    this.hedgeRowsData = options.hedgeRowsData || null;  // Hedging Demand: per-strike {price, hedge} rows per session date (/hedging-demand)
    this.t1RowsData = options.t1RowsData || null;  // T+1 exposure rows per session date (next session's book, evaluated live during the day)
    this.ivSkewData = options.ivSkewData || null;  // IV Skew: per-strike call/put IV rows per session date
    this.volumeRowsData = options.volumeRowsData || null;  // Options Volume: cumulative per-strike volume rows per session date
    this.crosshairRef = options.crosshairRef || null;  // Shared crosshair price ref from plugin
    this.anchorRef = options.anchorRef || null;  // Shared user-dragged anchor fractions {todayFrac, t1Frac, hedgeFrac} (null = default pin)
    this.anchorHitRef = options.anchorHitRef || null;  // Written each paint with drawn axis geometry (media px) for drag hit-testing
    this.series = series;
    this.chart = chart;
    this.options = {
      // GEX color configuration (blue = support, orange = resistance)
      // Each has a dark (low intensity) and bright (high intensity) endpoint
      positiveColor: options.positiveColor || [80, 165, 255],          // bright blue
      positiveColorDark: options.positiveColorDark || [15, 35, 90],    // dark blue
      negativeColor: options.negativeColor || [255, 155, 50],          // bright orange
      negativeColorDark: options.negativeColorDark || [110, 45, 5],    // dark orange
      // DEX color configuration (red = bullish pressure, green = bearish pressure)
      dexPositiveColor: options.dexPositiveColor || [255, 95, 95],     // bright red
      dexPositiveColorDark: options.dexPositiveColorDark || [110, 20, 20], // dark red
      dexNegativeColor: options.dexNegativeColor || [60, 220, 120],    // bright green
      dexNegativeColorDark: options.dexNegativeColorDark || [10, 75, 30],  // dark green
      supportLineColor: options.supportLineColor || [34, 211, 238],  // cyan
      resistanceLineColor: options.resistanceLineColor || [244, 63, 94], // pink
      maxOpacity: options.maxOpacity || 0.72,
      minOpacity: options.minOpacity || 0.40,

      // Dealer hedging indicators (per-strike net dealer hedge flow)
      showHedgingDemand: options.showHedgingDemand === true,  // Hedging demand heatmap

      // VEX color palette (purple = amplifying hedging flow, yellow = dampening hedging flow)
      vexPositiveColor: options.vexPositiveColor || [195, 120, 255],   // bright purple
      vexPositiveColorDark: options.vexPositiveColorDark || [65, 25, 110], // dark purple
      vexNegativeColor: options.vexNegativeColor || [255, 215, 65],    // bright yellow
      vexNegativeColorDark: options.vexNegativeColorDark || [110, 85, 10], // dark yellow
      // CEX color palette (cyan = delta growing, rose = delta decaying)
      cexPositiveColor: options.cexPositiveColor || [40, 210, 240],    // bright cyan
      cexPositiveColorDark: options.cexPositiveColorDark || [2, 70, 85],   // dark cyan
      cexNegativeColor: options.cexNegativeColor || [245, 60, 100],    // bright rose
      cexNegativeColorDark: options.cexNegativeColorDark || [100, 10, 30], // dark rose

      // Cell sizing
      cellPadding: options.cellPadding || 0,
      // Cell heights (ES points): FALLBACK only for single-level columns;
      // heatmap columns normally self-size from the row's strike spacing.
      cellHeight: options.cellHeight || 2.0,
      dexCellHeight: options.dexCellHeight || 2.0,
      vexCellHeight: options.vexCellHeight || 2.0,
      cexCellHeight: options.cexCellHeight || 2.0,
      oiBarMaxWidth: options.oiBarMaxWidth || 450,   // Max OI bar width in logical pixels

      // Exposure Profile heatmap (time by strike matrix over the exposure rows)
      showExpProfileHeatmap: options.showExpProfileHeatmap === true,

      // Live vs historic mode (hide direction arrows for historic days)
      isLiveDay: options.isLiveDay !== false,

      ...options
    };
  }

  /**
   * Convert intensity to RGBA via a three-stop hue spectrum per sign:
   * dark -> bright (at 0.55) -> hot (hue-shifted toward yellow, at 1.0).
   * Magnitude reads primarily through the color ramp; opacity stays in a
   * narrow band (options.minOpacity..maxOpacity, default 0.40-0.72)
   * so cells are always visible without dominating the chart.
   */
  _intensityToColor(intensity, darkPos, brightPos, darkNeg, brightNeg) {
    let darkRgb, brightRgb;
    if (intensity > 0.001) {
      darkRgb = darkPos;
      brightRgb = brightPos;
    } else if (intensity < -0.001) {
      darkRgb = darkNeg;
      brightRgb = brightNeg;
    } else {
      return 'transparent';
    }
    const absI = Math.abs(intensity);
    const BRIGHT_POS = 0.55;  // spectrum position of the bright stop
    let rgb;
    if (absI <= BRIGHT_POS) {
      const t = absI / BRIGHT_POS;
      rgb = darkRgb.map((d, i) => Math.round(d + (brightRgb[i] - d) * t));
    } else {
      const t = (absI - BRIGHT_POS) / (1 - BRIGHT_POS);
      const hot = hotStopFor(brightRgb);
      rgb = brightRgb.map((b, i) => Math.round(b + (hot[i] - b) * t));
    }
    const minO = this.options.minOpacity ?? 0.55;
    const maxO = this.options.maxOpacity ?? 0.85;
    const opacity = minO + (maxO - minO) * absI;
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity.toFixed(3)})`;
  }

  _getColor(intensity) {
    return this._intensityToColor(intensity,
      this.options.positiveColorDark, this.options.positiveColor,
      this.options.negativeColorDark, this.options.negativeColor);
  }

  _getColorDex(intensity) {
    return this._intensityToColor(intensity,
      this.options.dexPositiveColorDark, this.options.dexPositiveColor,
      this.options.dexNegativeColorDark, this.options.dexNegativeColor);
  }

  _getColorVex(intensity) {
    return this._intensityToColor(intensity,
      this.options.vexPositiveColorDark, this.options.vexPositiveColor,
      this.options.vexNegativeColorDark, this.options.vexNegativeColor);
  }

  _getColorCex(intensity) {
    return this._intensityToColor(intensity,
      this.options.cexPositiveColorDark, this.options.cexPositiveColor,
      this.options.cexNegativeColorDark, this.options.cexNegativeColor);
  }

  /**
   * Get OI bar color, fully saturated (width encodes magnitude).
   * @param {number} intensity - Signed intensity [-1, 1]
   * @param {'gex'|'dex'|'vex'} type - Which Greek
   * @param {number} [opacity=0.65] - Alpha channel; callers may use it as a
   *   second magnitude channel (see the OI wall path).
   * @returns {string} RGBA color string
   */
  _getOiBarColor(intensity, type, opacity = 0.65) {
    // Color keys on SIGN only; magnitude is expressed by bar width (and the
    // caller's presence-tick opacity). A magnitude gate here would silently
    // blank the 0DTE tail, whose raw intensities sit far below any epsilon.

    let baseRgb;
    if (type === 'gex') {
      baseRgb = intensity > 0 ? this.options.positiveColor : this.options.negativeColor;
    } else if (type === 'dex') {
      baseRgb = intensity > 0 ? this.options.dexPositiveColor : this.options.dexNegativeColor;
    } else if (type === 'cex') {
      baseRgb = intensity > 0 ? this.options.cexPositiveColor : this.options.cexNegativeColor;
    } else if (type === 'hedge') {
      baseRgb = intensity > 0 ? [60, 220, 120] : [255, 95, 95];
    } else if (type === 'call_oi') {
      baseRgb = [74, 222, 128];
    } else if (type === 'put_oi') {
      baseRgb = [248, 113, 113];
    } else if (type === 'call_sell') {
      // Options Volume aggressor views (DEALER perspective): dealer-sold
      // calls (at-ask prints), violet. Dealer-bought legs reuse
      // call_oi/put_oi, so the classic call-green / put-red reads as the
      // dealer BUYING that instrument and the cool hue (violet calls /
      // sky puts) as the dealer selling it.
      baseRgb = [192, 132, 252];
    } else if (type === 'put_sell') {
      // Dealer-sold puts (at-ask prints), sky blue
      baseRgb = [56, 189, 248];
    } else if (type === 'net_oi') {
      // Net takes the dominant side's color: green when calls exceed puts
      // (bar extends right), red when puts exceed calls (bar extends left).
      baseRgb = intensity > 0 ? [74, 222, 128] : [248, 113, 113];
    } else {
      baseRgb = intensity > 0 ? this.options.vexPositiveColor : this.options.vexNegativeColor;
    }
    return `rgba(${baseRgb[0]}, ${baseRgb[1]}, ${baseRgb[2]}, ${opacity})`;
  }

  /**
   * Draw a single OI horizontal bar extending rightward from anchor X
   */
  _drawSingleOiBar(ctx, y, barHeight, intensity, anchorX, maxWidth, type, hPixRatio, opacity = 0.65) {
    if (intensity === 0) return;

    // Clamp, never cull: any nonzero exposure renders at least 1 physical px.
    // The caller decides what to show; sub-pixel widths become a 1px tick
    // (dimmed by the caller via opacity so it reads as presence, not
    // magnitude).
    const barWidth = Math.max(1, Math.abs(intensity) * maxWidth * hPixRatio);

    ctx.fillStyle = this._getOiBarColor(intensity, type, opacity);
    // Diverging around the zero axis: positive bars extend RIGHT of the
    // anchor, negative bars extend LEFT (sign is also in the color).
    const x = intensity > 0 ? Math.round(anchorX) : Math.round(anchorX - barWidth);
    ctx.fillRect(
      x,
      Math.round(y),
      Math.round(barWidth),
      Math.max(Math.round(barHeight), 1)
    );
  }

  /**
   * Open Interest distribution: two-sided per-strike histogram of the raw
   * settled OI book. Calls (green) extend RIGHT of a vertical zero axis, puts
   * (red) LEFT, one bar pair per strike, plus an optional thinner Net bar
   * (call minus put; side and color follow the dominant leg). Completed sessions
   * anchor at their Globex open (candle-anchored, not draggable); the
   * developing session draws on its own screen-fixed draggable axis (chip
   * "OI", anchorRef.oiFrac; renko keeps the left-edge anchor but is never
   * draggable, same exclusion as _profileAnchorX). Widths are LINEAR in OI
   * against a shared call/put max per session; sub-pixel nonzero bars render
   * as a dim 1px presence tick via _drawSingleOiBar.
   */
  _drawOiDistribution(ctx, timeScale, hPixRatio, vPixRatio, bitmapWidth, bitmapHeight) {
    if (!this.oiBarsData) return;

    const toggles = this.options.oiDistToggles || {};
    const showCall = toggles.callOi !== false;
    const showPut = toggles.putOi !== false;
    const showNet = toggles.net !== false;
    if (!showCall && !showPut && !showNet) return;

    const now = Math.floor(Date.now() / 1000);
    const GLOBEX_DURATION = 82800;

    const sessions = Object.values(this.oiBarsData)
      .filter(s => s.globex_open_ts)
      .sort((a, b) => a.globex_open_ts - b.globex_open_ts);

    const classified = sessions.map(s => {
      const globexClose = s.globex_open_ts + GLOBEX_DURATION;
      return {
        ...s,
        globexClose,
        isCompleted: globexClose <= now,
        isDeveloping: s.globex_open_ts <= now && now < globexClose,
        isFuture: globexClose > now && s.globex_open_ts > now,
      };
    });

    let developingIdx = classified.findIndex(s => s.isDeveloping);
    if (developingIdx === -1) developingIdx = classified.findIndex(s => s.isFuture);

    const isRenko = (this.options.timeframe || '').includes('renko');
    const halfBudget = (this.options.oiBarMaxWidth || 450) / 2;

    for (let i = 0; i < classified.length; i++) {
      const session = classified[i];
      if (session.isFuture && i !== developingIdx) continue;

      const bars = session.bars || [];
      if (bars.length === 0) continue;

      const globexX = timeScale.timeToCoordinate(session.globex_open_ts);
      const rthX = timeScale.timeToCoordinate(session.rth_open_ts);

      // Per-side width budget: half the configured max, capped so the
      // left-extending put bars stay inside this session's Globex-to-RTH
      // gap instead of running over the prior session's column.
      let perSideMax = halfBudget;
      if (globexX !== null && rthX !== null && rthX > globexX) {
        perSideMax = Math.min(perSideMax, (rthX - globexX) * 0.45);
      }
      if (perSideMax <= 0) continue;

      let anchorX;
      let pinned = false;
      if (i === developingIdx) {
        const frac = this.anchorRef?.oiFrac;
        pinned = !isRenko;
        if (pinned && frac != null) {
          anchorX = frac * bitmapWidth;
        } else {
          const visRange = timeScale.getVisibleLogicalRange();
          let anchorLogical = visRange ? timeScale.logicalToCoordinate(visRange.from) : null;
          if (anchorLogical == null) anchorLogical = 0;
          anchorX = anchorLogical * hPixRatio + (perSideMax + 4) * hPixRatio;
        }
      } else if (globexX !== null) {
        anchorX = globexX * hPixRatio;
      } else {
        continue;
      }

      // Shared max across calls AND puts so the two sides are directly
      // comparable; |call - put| is bounded by it, so Net rides the same
      // scale.
      let sharedMax = 0;
      for (const bar of bars) {
        if ((bar.call_oi || 0) > sharedMax) sharedMax = bar.call_oi;
        if ((bar.put_oi || 0) > sharedMax) sharedMax = bar.put_oi;
      }
      if (sharedMax <= 0) continue;

      const extent = this._drawProfileAxis(ctx, bars, anchorX, vPixRatio);
      if (i === developingIdx) {
        this._recordAnchorHit('oi', { x: anchorX, pinned }, extent, hPixRatio, vPixRatio);
        if (pinned && extent) {
          this._drawAnchorLabel(ctx, 'OI', anchorX, bitmapHeight, hPixRatio, vPixRatio);
        }
      }

      const baseOpacity = session.isCompleted ? 0.5 : 0.75;
      const callPutH = 4;
      const netH = 3;
      const gap = 3;
      const totalH = callPutH + (showNet ? gap + netH : 0);

      for (const bar of bars) {
        const strikeY = this.series.priceToCoordinate(bar.price);
        if (strikeY === null) continue;
        const topY = Math.round(strikeY * vPixRatio) - totalH / 2;

        const callN = (bar.call_oi || 0) / sharedMax;
        const putN = (bar.put_oi || 0) / sharedMax;
        const netN = ((bar.call_oi || 0) - (bar.put_oi || 0)) / sharedMax;

        // Same as the sibling profiles: linear widths, sub-pixel nonzero
        // bars clamp to a dim 1px presence tick.
        const op = (n) => (Math.abs(n) * perSideMax * hPixRatio < 1 ? 0.3 : baseOpacity);
        if (showCall) {
          this._drawSingleOiBar(ctx, topY, callPutH, callN, anchorX, perSideMax, 'call_oi', hPixRatio, op(callN));
        }
        if (showPut) {
          this._drawSingleOiBar(ctx, topY, callPutH, -putN, anchorX, perSideMax, 'put_oi', hPixRatio, op(putN));
        }
        if (showNet) {
          this._drawSingleOiBar(ctx, topY + callPutH + gap, netH, netN, anchorX, perSideMax, 'net_oi', hPixRatio, op(netN));
        }
      }
    }
  }

  /**
   * Options Volume: two-sided per-strike traded-volume histogram (calls
   * green RIGHT, puts red LEFT, thinner Net = calls minus puts; the OI
   * distribution's visual family) but crosshair-scrubbed like the
   * exposure family via _selectTargetRow: the forming candle by default,
   * the hovered candle's window on hover. Rows are day-to-date CUMULATIVE,
   * so the per-candle volume is the target row minus the previous row
   * with data in the same session; a hovered window with no row of its
   * own draws nothing (zero traded volume). The parent-gated Cumulative
   * sub-toggle draws a SECOND histogram of the raw cumulative row on its
   * own draggable axis (chip "CV"); the stale-window guard does NOT apply
   * there, a running total is valid at any at-or-before row.
   */
  _drawOptionsVolume(ctx, timeScale, hPixRatio, vPixRatio, bitmapWidth, bitmapHeight) {
    const sessions = this.volumeRowsData;
    if (!sessions) return;

    const toggles = this.options.optVolToggles || {};
    const show = {
      showCall: toggles.callVol !== false,
      showPut: toggles.putVol !== false,
      showNet: toggles.net !== false,
    };
    if (!show.showCall && !show.showPut && !show.showNet) return;

    // Own bucket width for the retarget guard: exposureRowsTfSec is unset
    // when the Exposure Profile is off (the ivSkewTfSec precedent). This
    // runs on the RENDERER, which has no _windowsTfSec; derive the
    // fallback from the timeframe option directly.
    const winSec = this.options.optVolTfSec || TF_SECONDS[this.options.timeframe] || 1800;
    const { targetCandle, targetDateKey } = this._selectTargetRow(sessions, winSec);
    if (!targetCandle) return;

    const maxWidth = PROFILE_MAX_WIDTH;

    // Per-candle pass ("OV" axis): cumulative diff vs the previous row in
    // the same session. Stale-window zero guard: the cursor sitting past
    // the end of the selected row's window means the hovered window had
    // no trades, so draw nothing instead of attributing the earlier
    // window's volume to it (_selectTargetRow's open-session retarget
    // covers the cross-session case; this is the within-session analog).
    const crosshairTime = this.crosshairRef?.time;
    const rowIsStale = crosshairTime != null && crosshairTime >= targetCandle.time + winSec;
    if (!rowIsStale) {
      const candles = sessions[targetDateKey]?.candles || [];
      const idx = candles.findIndex(c => c.time === targetCandle.time);
      const prev = idx > 0 ? candles[idx - 1] : null;
      const prevByPrice = new Map((prev?.levels || []).map(l => [l.price, l]));
      const diffLevels = [];
      for (const lv of (targetCandle.levels || [])) {
        const p = prevByPrice.get(lv.price);
        // Cumulative is monotone within a session; the clamp keeps a
        // boundary mislabel from painting phantom bars.
        const d = (field) => Math.max(0, (lv[field] || 0) - ((p && p[field]) || 0));
        const callV = d('call_vol');
        const putV = d('put_vol');
        if (callV > 0 || putV > 0) {
          diffLevels.push({
            price: lv.price, call_vol: callV, put_vol: putV,
            call_vol_ask: d('call_vol_ask'), call_vol_bid: d('call_vol_bid'),
            put_vol_ask: d('put_vol_ask'), put_vol_bid: d('put_vol_bid'),
          });
        }
      }
      if (diffLevels.length) {
        const anchor = this._profileAnchorX(timeScale, targetCandle, targetDateKey, maxWidth, hPixRatio, bitmapWidth,
          this.anchorRef?.volFrac, 3 * (PROFILE_MAX_WIDTH + 8));
        if (anchor !== null) {
          const extent = this._drawVolumeBars(ctx, diffLevels, anchor.x, maxWidth, show, hPixRatio, vPixRatio);
          this._recordAnchorHit('vol', anchor, extent, hPixRatio, vPixRatio);
          if (anchor.pinned && extent) {
            this._drawAnchorLabel(ctx, 'OV', anchor.x, bitmapHeight, hPixRatio, vPixRatio);
          }
        }
      }
    }

    // Cumulative pass ("CV" axis): the raw cumulative row itself. Always
    // screen-pinned (forcePinned), historic hovers included: a day-level
    // running total is not "at" a candle, and candle-anchoring it would
    // overlap the per-candle histogram at the same x (user call
    // 2026-08-10). Data still retargets with the hover.
    if (this.options.optVolCumulative) {
      const cumLevels = (targetCandle.levels || []).filter(
        lv => (lv.call_vol || 0) > 0 || (lv.put_vol || 0) > 0);
      if (cumLevels.length) {
        const cumAnchor = this._profileAnchorX(timeScale, targetCandle, targetDateKey, maxWidth, hPixRatio, bitmapWidth,
          this.anchorRef?.volCumFrac, 4 * (PROFILE_MAX_WIDTH + 8), true);
        if (cumAnchor !== null) {
          const extent = this._drawVolumeBars(ctx, cumLevels, cumAnchor.x, maxWidth, show, hPixRatio, vPixRatio);
          this._recordAnchorHit('volcum', cumAnchor, extent, hPixRatio, vPixRatio);
          if (cumAnchor.pinned && extent) {
            this._drawAnchorLabel(ctx, 'CV', cumAnchor.x, bitmapHeight, hPixRatio, vPixRatio);
          }
        }
      }
    }
  }

  /**
   * One two-sided volume histogram (calls right / puts left / thin Net)
   * at the given anchor: the OI distribution's bar stack over
   * {price, call_vol, put_vol, ...} levels, reusing its color types and a
   * shared max normalization so both sides stay directly comparable.
   * Buy/sell split mode (optVolSplit) swaps the single row per strike for
   * PAIRED rows on a four-color scheme, read from the DEALER perspective
   * (the passive side of the classified aggressor, per the
   * trade_classification convention; the stored fields stay tape-side):
   * dealer buys (at-bid prints) above in the instrument's classic hue
   * (call green / put red, matching the totals view), dealer sells
   * (at-ask prints) below in the cool contrast hue (call violet / put
   * sky blue); rows predating VOLROW v2 (no split fields) and rows with
   * nothing classified fall back to the totals layout. Flow-net mode
   * (optVolFlowNet, precedence over the split view) draws ONE signed
   * residual bar per side instead: length = |at-bid minus at-ask| on the
   * residuals' OWN shared scale (a balanced strike collapses to nothing,
   * skew pops in proportion), classic hue when net dealer buying, cool
   * hue when net dealer selling. The thin Net row (total
   * calls minus puts) renders beneath in every mode, normalized by the
   * totals max so its scale never jumps when views toggle. Returns the
   * axis extent for drag geometry (null when nothing resolved).
   */
  _drawVolumeBars(ctx, levels, anchorX, maxWidth, show, hPixRatio, vPixRatio) {
    let totalsMax = 0;
    for (const lv of levels) {
      if ((lv.call_vol || 0) > totalsMax) totalsMax = lv.call_vol;
      if ((lv.put_vol || 0) > totalsMax) totalsMax = lv.put_vol;
    }
    if (totalsMax <= 0) return null;

    const hasSplitFields = levels.length > 0 && levels[0].call_vol_ask != null;

    let flowNet = !!this.options.optVolFlowNet && hasSplitFields;
    let flowMax = 0;
    if (flowNet) {
      for (const lv of levels) {
        const n = Math.abs((lv.call_vol_ask || 0) - (lv.call_vol_bid || 0));
        const m = Math.abs((lv.put_vol_ask || 0) - (lv.put_vol_bid || 0));
        if (n > flowMax) flowMax = n;
        if (m > flowMax) flowMax = m;
      }
      if (flowMax <= 0) flowNet = false;
    }

    let split = !flowNet && !!this.options.optVolSplit && hasSplitFields;
    let splitMax = 0;
    if (split) {
      for (const lv of levels) {
        if ((lv.call_vol_ask || 0) > splitMax) splitMax = lv.call_vol_ask;
        if ((lv.call_vol_bid || 0) > splitMax) splitMax = lv.call_vol_bid;
        if ((lv.put_vol_ask || 0) > splitMax) splitMax = lv.put_vol_ask;
        if ((lv.put_vol_bid || 0) > splitMax) splitMax = lv.put_vol_bid;
      }
      if (splitMax <= 0) split = false;
    }

    const extent = this._drawProfileAxis(ctx, levels, anchorX, vPixRatio);
    if (!extent) return null;

    const baseOpacity = 0.75;
    const callPutH = split ? 3 : 4;
    const splitGap = 1;
    const netH = 3;
    const gap = 3;
    const bodyH = split ? callPutH * 2 + splitGap : callPutH;
    const totalH = bodyH + (show.showNet ? gap + netH : 0);

    for (const lv of levels) {
      const strikeY = this.series.priceToCoordinate(lv.price);
      if (strikeY === null) continue;
      const topY = Math.round(strikeY * vPixRatio) - totalH / 2;

      // OI-distribution parity: linear widths, sub-pixel nonzero bars
      // clamp to a dim 1px presence tick.
      const op = (n) => (Math.abs(n) * maxWidth * hPixRatio < 1 ? 0.3 : baseOpacity);

      if (flowNet) {
        // DEALER perspective (trade_classification negation): dealer buys
        // what printed at the bid, sells what printed at the ask.
        const n = (lv.call_vol_bid || 0) - (lv.call_vol_ask || 0);
        const m = (lv.put_vol_bid || 0) - (lv.put_vol_ask || 0);
        const nN = Math.abs(n) / flowMax;
        const mN = Math.abs(m) / flowMax;
        if (show.showCall && n !== 0) {
          this._drawSingleOiBar(ctx, topY, callPutH, nN, anchorX, maxWidth,
            n > 0 ? 'call_oi' : 'call_sell', hPixRatio, op(nN));
        }
        if (show.showPut && m !== 0) {
          this._drawSingleOiBar(ctx, topY, callPutH, -mN, anchorX, maxWidth,
            m > 0 ? 'put_oi' : 'put_sell', hPixRatio, op(mN));
        }
      } else if (split) {
        // DEALER perspective: top row = dealer buys (at-bid prints,
        // classic hue), bottom row = dealer sells (at-ask prints, cool hue)
        const cBuy = (lv.call_vol_bid || 0) / splitMax;
        const cSell = (lv.call_vol_ask || 0) / splitMax;
        const pBuy = (lv.put_vol_bid || 0) / splitMax;
        const pSell = (lv.put_vol_ask || 0) / splitMax;
        if (show.showCall) {
          this._drawSingleOiBar(ctx, topY, callPutH, cBuy, anchorX, maxWidth, 'call_oi', hPixRatio, op(cBuy));
          this._drawSingleOiBar(ctx, topY + callPutH + splitGap, callPutH, cSell, anchorX, maxWidth, 'call_sell', hPixRatio, op(cSell));
        }
        if (show.showPut) {
          this._drawSingleOiBar(ctx, topY, callPutH, -pBuy, anchorX, maxWidth, 'put_oi', hPixRatio, op(pBuy));
          this._drawSingleOiBar(ctx, topY + callPutH + splitGap, callPutH, -pSell, anchorX, maxWidth, 'put_sell', hPixRatio, op(pSell));
        }
      } else {
        const callN = (lv.call_vol || 0) / totalsMax;
        const putN = (lv.put_vol || 0) / totalsMax;
        if (show.showCall) {
          this._drawSingleOiBar(ctx, topY, callPutH, callN, anchorX, maxWidth, 'call_oi', hPixRatio, op(callN));
        }
        if (show.showPut) {
          this._drawSingleOiBar(ctx, topY, callPutH, -putN, anchorX, maxWidth, 'put_oi', hPixRatio, op(putN));
        }
      }
      if (show.showNet) {
        const netN = ((lv.call_vol || 0) - (lv.put_vol || 0)) / totalsMax;
        this._drawSingleOiBar(ctx, topY + bodyH + gap, netH, netN, anchorX, maxWidth, 'net_oi', hPixRatio, op(netN));
      }
    }
    return extent;
  }

  /**
   * Shared target-row selection for the Exposure Profile, Hedging Demand,
   * T+1 profile and IV Skew: the candle at or just before the crosshair
   * time; with NO crosshair, fall back to the LATEST candle (the
   * developing period, kept fresh by the live SSE row). Also reports
   * which session dateKey the chosen candle came from, so the anchor
   * logic can tell the open session apart from completed ones. When the
   * crosshair sits PAST the end of the hovered candle's window (i.e.,
   * over the open session's candles, which may have no at-or-before row
   * yet: overnight only the forming bucket exists), retarget to the OPEN
   * session so the current-day profile stays visible instead of falling
   * back to the prior day's last row. winSecOverride is the caller's
   * bucket width: the exposure-family containers omit it (their rows are
   * server-bucketed to exposureRowsTfSec), the IV Skew passes its own
   * client-side bucket (ivSkewTfSec), which can differ when the
   * exposure fetch never ran.
   */
  _selectTargetRow(sessions, winSecOverride) {
    const crosshairTime = this.crosshairRef?.time;
    let targetCandle = null;
    let targetDateKey = null;
    let latestCandle = null;
    let latestDateKey = null;
    for (const dateKey of Object.keys(sessions)) {
      const candles = sessions[dateKey]?.candles || [];
      for (let i = candles.length - 1; i >= 0; i--) {
        const c = candles[i];
        if (c.time == null) continue;
        if (!latestCandle || c.time > latestCandle.time) {
          latestCandle = c;
          latestDateKey = dateKey;
        }
        if (crosshairTime != null && c.time <= crosshairTime) {
          if (!targetCandle || c.time > targetCandle.time) {
            targetCandle = c;
            targetDateKey = dateKey;
          }
          break;
        }
      }
    }
    if (!targetCandle) {
      targetCandle = latestCandle;
      targetDateKey = latestDateKey;
    }

    const openDate = getOpenSessionDate();
    if (crosshairTime != null && targetCandle && openDate
        && targetDateKey !== openDate && sessions[openDate]?.candles?.length) {
      const winSec = winSecOverride || this.options.exposureRowsTfSec || 60;
      if (crosshairTime >= targetCandle.time + winSec) {
        const oc = sessions[openDate].candles;
        let cand = null;
        for (let i = oc.length - 1; i >= 0; i--) {
          if (oc[i].time <= crosshairTime) { cand = oc[i]; break; }
        }
        // No open-session row at-or-before the cursor (overnight there is
        // only the forming bucket): show the live forming row.
        targetCandle = cand || oc[oc.length - 1];
        targetDateKey = openDate;
      }
    }
    return { targetCandle, targetDateKey };
  }

  /**
   * Anchor X (bitmap px) for the Exposure Profile / Hedging Demand column.
   * While the target row belongs to the OPEN session, the column is pinned
   * screen-fixed: at the caller's user-dragged pane fraction (frac) when
   * set, else to the left edge of the visible range (same zone as the
   * developing projected curves), offset inward by the full bar span so the
   * left-extending exposure bars land on screen; hover still scrubs the
   * DATA, only the position is pinned. Parameterized for both the today
   * (Exposure Profile) and hedge (Hedging Demand) axes: frac is the axis's
   * own dragged fraction, extraLogicalPx shifts its DEFAULT pin right so
   * the two default side by side instead of overlapping. Completed
   * sessions (and everything once the session closes) anchor at the target
   * candle's time. Renko keeps the candle-time anchor: its axis is
   * de-collided fake seconds, so session classification there is
   * unreliable. forcePinned keeps the axis screen-fixed for HISTORIC
   * targets too (the Options Volume cumulative axis: a day-level series
   * that would otherwise overlap the per-candle histogram at the hovered
   * candle); renko still falls through to the candle anchor. Returns
   * { x, pinned } (pinned = screen-fixed, i.e. draggable) or null when
   * the candle-time anchor is off-grid (caller skips the draw, current
   * behavior).
   */
  _profileAnchorX(timeScale, targetCandle, targetDateKey, maxWidth, hPixRatio, bitmapWidth, frac, extraLogicalPx = 0, forcePinned = false) {
    const openSessionDate = getOpenSessionDate();
    const isRenko = (this.options.timeframe || '').includes('renko');
    if ((forcePinned || (openSessionDate != null && targetDateKey === openSessionDate)) && !isRenko) {
      if (frac != null) {
        return { x: frac * bitmapWidth, pinned: true };
      }
      const visRange = timeScale.getVisibleLogicalRange();
      let anchorLogical = visRange ? timeScale.logicalToCoordinate(visRange.from) : null;
      if (anchorLogical == null) anchorLogical = 0;
      return { x: anchorLogical * hPixRatio + (maxWidth + 4 + extraLogicalPx) * hPixRatio, pinned: true };
    }
    const anchorLogical = timeScale.timeToCoordinate(targetCandle.time);
    if (anchorLogical === null) return null;
    return { x: anchorLogical * hPixRatio, pinned: false };
  }

  /**
   * Exposure Profile: per-strike OI exposure at the CURRENT spot and current
   * remaining time (the 1m exposure row). Default target is the LATEST row
   * (the developing period, kept fresh by the live SSE row); hovering a past
   * column shows that window's LAST row (the profile as of then). While the
   * session is OPEN, current-session rows draw pinned at the left edge
   * (_profileAnchorX); completed sessions anchor at the hovered candle. One
   * diverging bar per enabled greek per strike (positive right, negative
   * left of the shared zero axis), normalized per
   * greek within the target row, colored with the flow-layer palette via
   * _drawSingleOiBar. Mode 'dealer' renders the dealer-weighted fields,
   * 'absolute' the pure-OI grip (u_*).
   */
  _drawExposureHistogram(ctx, timeScale, hPixRatio, vPixRatio, bitmapWidth, bitmapHeight) {
    const sessions = this.exposureRowsData;
    if (!sessions) return;

    const { targetCandle, targetDateKey } = this._selectTargetRow(sessions);
    if (!targetCandle) return;

    const levels = targetCandle.levels || [];
    if (levels.length === 0) return;

    const maxWidth = PROFILE_MAX_WIDTH;  // logical units for _drawSingleOiBar
    const anchor = this._profileAnchorX(timeScale, targetCandle, targetDateKey, maxWidth, hPixRatio, bitmapWidth,
      this.anchorRef?.todayFrac, 0);
    if (anchor === null) return;

    const extent = this._drawProfileBars(ctx, levels, anchor.x, maxWidth, hPixRatio, vPixRatio);
    this._recordAnchorHit('today', anchor, extent, hPixRatio, vPixRatio);
    if (extent) {
      this._drawAnchorLabel(ctx, 'T', anchor.x, bitmapHeight, hPixRatio, vPixRatio);
      this._todayLabelDrawn = true;
    }
  }

  /**
   * Faint vertical zero-axis for the diverging profile bars, spanning the
   * row's strike range (positive bars extend right of it, negative left).
   * Returns the drawn extent { minY, maxY } in bitmap px (null when no
   * strike resolves to a coordinate) so callers can record drag geometry.
   */
  _drawProfileAxis(ctx, levels, anchorX, vPixRatio) {
    let minY = Infinity, maxY = -Infinity;
    for (const level of levels) {
      const y = this.series.priceToCoordinate(level.price);
      if (y === null) continue;
      const yPix = y * vPixRatio;
      if (yPix < minY) minY = yPix;
      if (yPix > maxY) maxY = yPix;
    }
    if (minY > maxY) return null;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.fillRect(Math.round(anchorX), Math.round(minY - 6), 1, Math.round(maxY - minY + 12));
    return { minY, maxY };
  }

  /**
   * Record a drawn, screen-fixed (draggable) axis into the shared hit ref
   * in MEDIA px so the chart component can hit-test raw pointer events
   * against it. Candle-anchored axes (pinned=false) are not draggable and
   * clear the entry instead.
   */
  _recordAnchorHit(kind, anchor, extent, hPixRatio, vPixRatio) {
    if (!this.anchorHitRef) return;
    if (!anchor?.pinned || !extent) {
      this.anchorHitRef[kind] = null;
      return;
    }
    this.anchorHitRef[kind] = {
      x: anchor.x / hPixRatio,
      minY: extent.minY / vPixRatio,
      maxY: extent.maxY / vPixRatio,
    };
  }

  /**
   * Small identifying chip ("T" / "T+1") at the bottom of a profile zero
   * axis, just above the time axis, so the user-positionable histograms
   * stay tellable apart. Same chip styling as the OI level labels.
   */
  _drawAnchorLabel(ctx, text, anchorX, bitmapHeight, hPixRatio, vPixRatio) {
    const fontSize = Math.round(10 * hPixRatio);
    ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const textWidth = ctx.measureText(text).width;
    const padding = 3 * hPixRatio;
    const y = bitmapHeight - 4 * vPixRatio;
    ctx.fillStyle = 'rgba(15, 20, 25, 0.85)';
    ctx.fillRect(
      anchorX - textWidth / 2 - padding,
      y - fontSize - padding,
      textWidth + padding * 2,
      fontSize + padding * 2
    );
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.fillText(text, anchorX, y);
  }

  /**
   * Shared Exposure Profile bar stack: one diverging bar per enabled greek
   * per strike (positive extends RIGHT of the zero axis, negative LEFT;
   * sign is also in the color), per-greek max-abs normalized within the
   * given row, honoring exposureProfileMode ('dealer' fields / 'absolute' u_*).
   * Widths are LINEAR in exposure; strikes whose bar would be sub-pixel
   * render as a dim ~1px presence tick (0DTE decays over ~100 orders of
   * magnitude away from spot, so most stored strikes are in that tail).
   * Used by the main histogram (left-edge pinned while the session is open,
   * candle-time anchored otherwise) and the T+1 profile (right-edge pinned).
   */
  _drawProfileBars(ctx, levels, anchorX, maxWidth, hPixRatio, vPixRatio) {
    const absolute = (this.options.exposureProfileMode || 'dealer') === 'absolute';
    const toggles = this.options.expProfileToggles || {};
    const greeks = [
      { key: 'gex', field: absolute ? 'u_gex' : 'gex', on: toggles.gex !== false },
      { key: 'dex', field: absolute ? 'u_dex' : 'dex', on: toggles.dex !== false },
      { key: 'vex', field: absolute ? 'u_vex' : 'vex', on: toggles.vex !== false },
      { key: 'cex', field: absolute ? 'u_cex' : 'cex', on: toggles.cex !== false },
    ].filter(g => g.on);
    if (greeks.length === 0) return;

    // Local per-greek normalization within the target row: each greek's
    // largest bar spans the full budget regardless of cross-greek scale.
    const maxAbs = {};
    for (const g of greeks) {
      let m = 0;
      for (const level of levels) {
        const av = Math.abs(level[g.field] || 0);
        if (av > m) m = av;
      }
      maxAbs[g.key] = m;
    }

    const barHeight = 4;
    const gap = 3;
    const totalHeight = greeks.length * barHeight + (greeks.length - 1) * gap;

    const axisExtent = this._drawProfileAxis(ctx, levels, anchorX, vPixRatio);

    for (const level of levels) {
      const strikeY = this.series.priceToCoordinate(level.price);
      if (strikeY === null) continue;

      let cursorY = Math.round(strikeY * vPixRatio) - totalHeight / 2;
      for (const g of greeks) {
        const m = maxAbs[g.key];
        const intensity = m > 0 ? (level[g.field] || 0) / m : 0;
        if (intensity !== 0) {
          // Widths stay LINEAR; _drawSingleOiBar clamps sub-pixel bars to a
          // 1px presence tick, dimmed here so it reads as presence, not
          // magnitude.
          const floored = Math.abs(intensity) * maxWidth * hPixRatio < 1;
          this._drawSingleOiBar(ctx, cursorY, barHeight, intensity, anchorX,
            maxWidth, g.key, hPixRatio, floored ? 0.3 : 0.65);
        }
        cursorY += barHeight + gap;
      }
    }
    return axisExtent;
  }

  /**
   * T+1 Exposure Profile: the NEXT session's per-strike profile (its book
   * evaluated at the live spot with the actual remaining time, a per-minute
   * series mirroring the today profile). The DATA follows the crosshair via
   * the shared target-row selection (crosshair over session D shows D's T+1
   * row at that time; no crosshair shows the live forming row); the axis
   * stays pinned one bar budget in from the right edge like the T+1
   * curves, bars diverge around it. Descriptive only, not a direction
   * call.
   */
  _drawT1ExposureProfile(ctx, timeScale, hPixRatio, vPixRatio, bitmapWidth, bitmapHeight) {
    const sessions = this.t1RowsData;
    if (!sessions) return;

    const { targetCandle } = this._selectTargetRow(sessions);
    const levels = targetCandle?.levels || [];
    if (levels.length === 0) return;

    const maxWidth = PROFILE_MAX_WIDTH;  // logical units for _drawSingleOiBar
    // Axis sits at the user-dragged pane fraction when set, else one bar
    // budget in from the right edge so positive (right-extending) bars
    // stay on screen.
    const t1Frac = this.anchorRef?.t1Frac;
    const anchorX = t1Frac != null
      ? t1Frac * bitmapWidth
      : bitmapWidth - (maxWidth + 2) * hPixRatio;
    const extent = this._drawProfileBars(ctx, levels, anchorX, maxWidth, hPixRatio, vPixRatio);
    this._recordAnchorHit('t1', { x: anchorX, pinned: true }, extent, hPixRatio, vPixRatio);
    if (extent) {
      this._drawAnchorLabel(ctx, 'T+1', anchorX, bitmapHeight, hPixRatio, vPixRatio);
    }
  }

  /**
   * Hedging Demand profile: one combined bar per strike from the served
   * hedge value
   * (net ES contracts dealers would trade over the next minute to stay
   * delta-neutral: gamma + vanna + charm hedge flow, dealer sign from the
   * flow-derived ratio). Same target-row and anchoring SEMANTICS as the
   * Exposure Profile (latest row by default, crosshair column when hovering
   * a past window; open-session rows screen-pinned via _profileAnchorX,
   * historic/closed rows and renko candle-anchored), but on its OWN
   * draggable zero axis (chip "HD", anchorRef.hedgeFrac): the default pin
   * sits one bar budget right of the Exposure Profile default so the two
   * profiles land side by side instead of overlapping. Bars diverge around
   * the axis (positive right, negative left). Green = net dealer
   * buy-to-hedge, red =
   * net dealer sell-to-hedge; descriptive magnitude of mechanical flow,
   * not a direction call. Widths are LINEAR; sub-pixel nonzero strikes
   * render as a dim ~1px presence tick (see _drawProfileBars).
   */
  /**
   * ES series close at-or-before t (binary search over the chart's own
   * bars). Null when no bar covers t.
   */
  _spotEsAtTime(t) {
    let data;
    try {
      data = this.series?.data?.() || [];
    } catch {
      return null;
    }
    let lo = 0, hi = data.length - 1, ans = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (data[mid].time <= t) { ans = data[mid]; lo = mid + 1; } else { hi = mid - 1; }
    }
    if (!ans) return null;
    const v = ans.close ?? ans.value;
    return (typeof v === 'number' && v > 0) ? v : null;
  }

  /**
   * ATM IV at-or-before t from the flattened quote-IV series the chart
   * component feeds in (options.quoteIvFlat, time-sorted). Null when the
   * series is absent or the nearest point is older than maxAgeSec.
   */
  _atmIvAtTime(t, maxAgeSec) {
    const arr = this.options.quoteIvFlat;
    if (!arr || !arr.length) return null;
    let lo = 0, hi = arr.length - 1, ans = null;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (arr[mid].time <= t) { ans = arr[mid]; lo = mid + 1; } else { hi = mid - 1; }
    }
    if (!ans) return null;
    if (maxAgeSec != null && (t - ans.time) > maxAgeSec) return null;
    return (typeof ans.value === 'number' && ans.value > 0) ? ans.value : null;
  }

  /**
   * The Exposure Profile candle matching a Hedging Demand candle (same
   * dateKey + window time): the greek components for the realized-mode
   * recompute live there since the 3.5 split ({price, hedge} levels carry
   * no components). Null when the exposure container is absent (Exposure
   * Profile off and realized off) or the window has no exposure column.
   */
  _exposureCandleAt(dateKey, time) {
    const session = this.exposureRowsData?.[dateKey];
    if (!session?.candles) return null;
    for (const c of session.candles) {
      if (c.time === time) return c;
    }
    return null;
  }

  /**
   * Realized Hedging Demand support: ES is excluded until the ES quote-IV
   * writer is hardened; its series re-selects the ATM strike/expiry set
   * tick-to-tick in the thin ES tape (median window |dIV| 4-8 vol pts,
   * sign-random; see test/FINDINGS-hd-vanna-charm-units.md). Gates the
   * resolver (per-unit fallback, HD? chip) and every realized-only fetch
   * and SSE ingest so ES pages do no dead work.
   */
  _realizedHedgeSupported() {
    return (this.options.underlying || 'spx') !== 'es';
  }

  /**
   * Realized-mode inputs for a Hedging Demand row: spotEs at the window
   * end, trailing dS and dIV over one display window (causal: at-or-before
   * lookups only, never past the hovered candle's window), plus the
   * price-keyed component map from the matching exposure candle (the
   * /hedging-demand levels are {price, hedge} only). Includes the
   * per-unit PARITY GATE: recomputing the served hedge from the matching
   * exposure components with dS=1 / dIV=0.01 and the series-derived
   * spotEs must reproduce it (rel 1e-3); where it cannot (renko bricks,
   * spot mismatch, exposure rows not fetched, ES underlying) realized
   * mode falls back to per-unit rather than render wrong magnitudes.
   * Returns { spotEs, dS, dIV, comp } or null (= fall back).
   */
  _resolveRealizedHedgeInputs(targetCandle, targetDateKey) {
    if (!this._realizedHedgeSupported()) return null;
    const expCandle = this._exposureCandleAt(targetDateKey, targetCandle.time);
    if (!expCandle) return null;
    const winSec = this.options.exposureRowsTfSec || 60;
    const t = targetCandle.time;
    const endT = t + winSec - 1;
    const spotEs = this._spotEsAtTime(endT);
    const prevSpot = this._spotEsAtTime(t - 1);
    if (spotEs == null || prevSpot == null) return null;
    // IV points may lag a minute; allow one window of staleness.
    const iv = this._atmIvAtTime(endT, 2 * winSec);
    const ivPrev = this._atmIvAtTime(t - 1, 2 * winSec);
    if (iv == null || ivPrev == null) return null;

    const comp = new Map();
    for (const lv of (expCandle.levels || [])) {
      comp.set(lv.price, lv);
    }

    // Parity gate on the row's largest served hedge value.
    let gateLevel = null;
    for (const level of (targetCandle.levels || [])) {
      if (level.hedge == null) continue;
      if (!gateLevel || Math.abs(level.hedge) > Math.abs(gateLevel.hedge)) {
        gateLevel = level;
      }
    }
    if (!gateLevel) return null;
    const gateComp = comp.get(gateLevel.price);
    if (!gateComp) return null;
    const perUnit = this._hedgeEsForLevel(gateComp, spotEs, 1.0, 0.01);
    const stored = gateLevel.hedge;
    if (perUnit == null || !(Math.abs(stored) > 0)) return null;
    if (Math.abs(perUnit - stored) > 1e-3 * Math.abs(stored)) return null;

    return { spotEs, dS: spotEs - prevSpot, dIV: iv - ivPrev, comp };
  }

  /**
   * Realized-mode hedge accessor for a Hedging Demand level: recompute
   * from the matching exposure components with the trailing dS/dIV; a
   * level without a component match falls back to its per-unit hedge.
   */
  _realizedHedgeVal(level, inputs) {
    const c = inputs.comp.get(level.price);
    if (!c) return level.hedge || 0;
    return this._hedgeEsForLevel(c, inputs.spotEs, inputs.dS, inputs.dIV) || 0;
  }

  /**
   * Per-level hedge ES contracts from exposure-level components:
   * -(gex/spotEs)*dS/cv - (vex*dIV)/cv - (cex*HEDGE_DT_CAL)/cv, cv =
   * spotEs*50. With dS=1, dIV=0.01 this reproduces the served hedge
   * value (the writer's per-unit convention, calculations/
   * greeks_heatmap.py build_exposure_row_payload); realized mode feeds
   * the actual trailing dS/dIV instead. Charm stays per-calendar-minute
   * in both modes (already time-realized).
   */
  _hedgeEsForLevel(level, spotEs, dS, dIV) {
    if (!(spotEs > 0)) return null;
    const cv = spotEs * 50;
    const gamma = -((level.gex || 0) / spotEs) * dS / cv;
    const vanna = -((level.vex || 0) * dIV) / cv;
    const charm = -((level.cex || 0) * HEDGE_DT_CAL) / cv;
    return gamma + vanna + charm;
  }

  _drawHedgeDemandProfile(ctx, timeScale, hPixRatio, vPixRatio, bitmapWidth, bitmapHeight) {
    const sessions = this.hedgeRowsData;
    if (!sessions) return;

    const { targetCandle, targetDateKey } = this._selectTargetRow(sessions);
    if (!targetCandle) return;

    const levels = targetCandle.levels || [];
    if (levels.length === 0) return;

    // Realized mode: swap the served per-unit hedge for the
    // fully-realized recompute (trailing dS and dIV over the displayed
    // window, components from the matching exposure candle). Falls back
    // to per-unit when inputs are unresolvable.
    let hedgeVal = (level) => level.hedge || 0;
    let realizedApplied = false;
    if (this.options.hedgeDemandRealized) {
      const inputs = this._resolveRealizedHedgeInputs(targetCandle, targetDateKey);
      if (inputs) {
        hedgeVal = (level) => this._realizedHedgeVal(level, inputs);
        realizedApplied = true;
      }
    }

    // Max-abs normalization within the target row: the largest hedge flow
    // (either sign) spans the full bar budget.
    let maxAbs = 0;
    for (const level of levels) {
      const av = Math.abs(hedgeVal(level));
      if (av > maxAbs) maxAbs = av;
    }
    if (maxAbs <= 0) return;

    const maxWidth = PROFILE_MAX_WIDTH;  // logical units for _drawSingleOiBar
    const anchor = this._profileAnchorX(timeScale, targetCandle, targetDateKey, maxWidth, hPixRatio, bitmapWidth,
      this.anchorRef?.hedgeFrac, PROFILE_MAX_WIDTH + 8);
    if (anchor === null) return;
    const anchorX = anchor.x;
    const barHeight = 4;

    const extent = this._drawProfileAxis(ctx, levels, anchorX, vPixRatio);
    this._recordAnchorHit('hedge', anchor, extent, hPixRatio, vPixRatio);
    // Pinned (open session) = own independent axis, always labeled "HD".
    // Candle-anchored (historic/closed/renko) = both profiles collapse onto
    // the target candle; skip the chip if the Exposure Profile already drew
    // its "T" there so the labels don't stack.
    // Chip: "HD" per-unit, "HD*" realized applied, "HD?" realized
    // requested but inputs unresolvable (per-unit values shown).
    const chip = this.options.hedgeDemandRealized ? (realizedApplied ? 'HD*' : 'HD?') : 'HD';
    if (extent && (anchor.pinned || !this._todayLabelDrawn)) {
      this._drawAnchorLabel(ctx, chip, anchorX, bitmapHeight, hPixRatio, vPixRatio);
    }

    for (const level of levels) {
      const intensity = hedgeVal(level) / maxAbs;
      if (intensity === 0) continue;

      const strikeY = this.series.priceToCoordinate(level.price);
      if (strikeY === null) continue;

      // Same as the Exposure Profile bars: linear widths, sub-pixel nonzero
      // hedge flow clamps to a dim 1px presence tick in _drawSingleOiBar.
      const floored = Math.abs(intensity) * maxWidth * hPixRatio < 1;
      const y = Math.round(strikeY * vPixRatio) - barHeight / 2;
      this._drawSingleOiBar(ctx, y, barHeight, intensity, anchorX,
        maxWidth, 'hedge', hPixRatio, floored ? 0.3 : 0.65);
    }
  }

  /**
   * IV Skew: the live engine's per-strike call/put IVs as
   * price-anchored dots on an own draggable zero axis (chip "IV",
   * anchorRef.smileFrac). Follows the Expiry Mode selector: 0dte =
   * nearest expiry, FULL = the whole book capped to the strikes nearest
   * spot, where one price can carry a dot per expiry. Calls cyan, puts rose
   * (support/resistance palette); a dot's distance from the axis is that
   * side's IV, normalized to the row's max IV so the smile SHAPE fills
   * the bar budget (the absolute level lives on the IV Index line). Held
   * points (side not freshly quoted that minute, fc/fp = 0) draw dimmed:
   * they are still the engine's real IV state, the alpha only marks
   * quote freshness. Same target-row and anchoring semantics as the
   * Exposure Profile (rows bucketed to the display tf at fetch, LAST 1m
   * row per window). Descriptive only, not a direction call.
   */
  _drawIvSkew(ctx, timeScale, hPixRatio, vPixRatio, bitmapWidth, bitmapHeight) {
    const sessions = this.ivSkewData;
    if (!sessions) return;

    const { targetCandle, targetDateKey } = this._selectTargetRow(sessions, this.options.ivSkewTfSec || 1800);
    if (!targetCandle) return;

    const points = targetCandle.points || [];
    if (points.length === 0) return;

    let maxIv = 0;
    for (const pt of points) {
      if (pt.c != null && pt.c > maxIv) maxIv = pt.c;
      if (pt.p != null && pt.p > maxIv) maxIv = pt.p;
    }
    if (maxIv <= 0) return;

    const maxWidth = PROFILE_MAX_WIDTH;  // logical units, same budget as the profiles
    // Default pin one further bar budget right of the Hedging Demand axis
    // so the three default side by side instead of overlapping.
    const anchor = this._profileAnchorX(timeScale, targetCandle, targetDateKey, maxWidth, hPixRatio, bitmapWidth,
      this.anchorRef?.smileFrac, 2 * (PROFILE_MAX_WIDTH + 8));
    if (anchor === null) return;
    const anchorX = anchor.x;

    const extent = this._drawProfileAxis(ctx, points, anchorX, vPixRatio);
    this._recordAnchorHit('smile', anchor, extent, hPixRatio, vPixRatio);
    if (extent && (anchor.pinned || !this._todayLabelDrawn)) {
      this._drawAnchorLabel(ctx, 'IV', anchorX, bitmapHeight, hPixRatio, vPixRatio);
    }

    const callRgb = this.options.supportLineColor;    // cyan
    const putRgb = this.options.resistanceLineColor;  // rose
    const radius = Math.max(1.5 * hPixRatio, 1.5);
    for (const pt of points) {
      const y = this.series.priceToCoordinate(pt.price);
      if (y === null) continue;
      const yPix = y * vPixRatio;
      if (pt.c != null) {
        const x = anchorX + (pt.c / maxIv) * maxWidth * hPixRatio;
        ctx.fillStyle = `rgba(${callRgb[0]}, ${callRgb[1]}, ${callRgb[2]}, ${pt.fc ? 0.9 : 0.35})`;
        ctx.beginPath();
        ctx.arc(x, yPix, radius, 0, Math.PI * 2);
        ctx.fill();
      }
      if (pt.p != null) {
        const x = anchorX + (pt.p / maxIv) * maxWidth * hPixRatio;
        ctx.fillStyle = `rgba(${putRgb[0]}, ${putRgb[1]}, ${putRgb[2]}, ${pt.fp ? 0.9 : 0.35})`;
        ctx.beginPath();
        ctx.arc(x, yPix, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Derived overlay (risk-neutral band): descriptive
    // readings of the row's fitted eSSVI slice
    // (test/RESEARCH-smile-levels.md). Additive: a failure here must
    // never break the dots.
    try {
      this._drawSmileOverlays(ctx, targetCandle, targetDateKey,
        hPixRatio, vPixRatio, bitmapWidth, bitmapHeight);
    } catch (e) { /* derived layers are best-effort */ }
  }

  /**
   * Per-row derived values for the distribution band, memoized on the
   * candle. All slice math runs in the row's native (SPX) k-space;
   * derived PRICES convert to ES with the same ratio the dots' prices
   * used (the placement ratio recorded on the candle when present, else
   * the day ratio). Missing essvi/ratio yields no band, never an error.
   */
  _deriveSmileOverlays(candle, dateKey) {
    const ratio = candle.ratio
      || Number((this.options.ratiosByDate || {})[dateKey]) || null;
    const cached = SMILE_DERIVED_CACHE.get(candle);
    if (cached && cached.ratio === ratio) return cached.d;

    const es = candle.essvi;
    const d = { ratio };

    if (es && es.theta > 0 && es.forward > 0 && es.tau > 1e-10 && ratio > 0) {
      d.haveFit = true;
      const toEs = (k) => (k === null ? null : es.forward * Math.exp(k) * ratio);
      d.p25 = toEs(percentileK(0.25, es));
      d.p50 = toEs(percentileK(0.50, es));
      d.p75 = toEs(percentileK(0.75, es));
      d.mode = toEs(densityModeK(es));
    }

    SMILE_DERIVED_CACHE.set(candle, { ratio, d });
    return d;
  }

  /** Thin horizontal line across the pane with a right-edge label chip. */
  _drawSmileHLine(ctx, price, stroke, dashed, label, bitmapWidth, hPixRatio, vPixRatio) {
    const y = this.series.priceToCoordinate(price);
    if (y === null) return;
    const yPix = y * vPixRatio;
    ctx.save();
    ctx.strokeStyle = stroke;
    ctx.lineWidth = Math.max(1, hPixRatio);
    if (dashed) ctx.setLineDash([4 * hPixRatio, 4 * hPixRatio]);
    ctx.beginPath();
    ctx.moveTo(0, yPix);
    ctx.lineTo(bitmapWidth, yPix);
    ctx.stroke();
    ctx.restore();
    if (label) {
      const fontSize = Math.round(9 * hPixRatio);
      ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'bottom';
      const tw = ctx.measureText(label).width;
      const pad = 2 * hPixRatio;
      ctx.fillStyle = 'rgba(15, 20, 25, 0.85)';
      ctx.fillRect(bitmapWidth - tw - pad * 3, yPix - fontSize - pad * 2, tw + pad * 2, fontSize + pad * 2);
      ctx.fillStyle = stroke;
      ctx.fillText(label, bitmapWidth - pad * 2, yPix - pad);
    }
  }

  /**
   * The derived overlay (distribution band), gated by its
   * sub-toggle. Descriptive only: labels carry no directional language;
   * the band is the RISK-NEUTRAL settlement distribution, central
   * region only.
   */
  _drawSmileOverlays(ctx, candle, dateKey,
    hPixRatio, vPixRatio, bitmapWidth, bitmapHeight) {
    const o = this.options;
    if (!o.ivSkewBand) return;
    const isFull = (o.expiryMode || '0dte') === 'full';
    const tag = isFull ? ' front' : '';
    const d = this._deriveSmileOverlays(candle, dateKey);

    // Risk-neutral central band: 25-75 fill + dashed median + mode tick
    if (o.ivSkewBand && d.haveFit && d.p25 !== null && d.p75 !== null) {
      const y25 = this.series.priceToCoordinate(d.p25);
      const y75 = this.series.priceToCoordinate(d.p75);
      if (y25 !== null && y75 !== null) {
        const top = Math.min(y25, y75) * vPixRatio;
        const bot = Math.max(y25, y75) * vPixRatio;
        ctx.fillStyle = 'rgba(148, 163, 184, 0.07)';
        ctx.fillRect(0, top, bitmapWidth, Math.max(bot - top, 1));
      }
      if (d.p50 !== null) {
        this._drawSmileHLine(ctx, d.p50, 'rgba(148, 163, 184, 0.55)', true,
          `RND${tag}`, bitmapWidth, hPixRatio, vPixRatio);
      }
      if (d.mode !== null) {
        this._drawSmileHLine(ctx, d.mode, 'rgba(148, 163, 184, 0.85)', false,
          null, bitmapWidth, hPixRatio, vPixRatio);
      }
    }
  }

  /**
   * Curve set for a session. Default: the session's static canonical bake
   * (settlement book re-baked once at expiry-minus-24h). With the live
   * Projected Curves sub-layer on, hovering a time inside a session that
   * has stored 1-min live snapshots scrubs to the state nearest
   * at-or-before the cursor; without a hover the session entry already
   * carries the latest live curves for today (_effectiveOiCurvesData).
   */
  _curveSetForSession(session) {
    if (!this.options.projCurvesLive || !session.date) return session;
    const hist = (this.options.liveCurvesHistory || {})[session.date];
    if (!hist || !hist.length) return session;
    const t = this.crosshairRef?.time;
    const gOpen = session.globex_open_ts;
    if (t == null || !gOpen || t < gOpen || t >= gOpen + 82800) return session;
    let pick = null;
    for (let i = hist.length - 1; i >= 0; i--) {
      if (hist[i].time <= t) { pick = hist[i]; break; }
    }
    if (!pick) pick = hist[0];
    const c = pick.curves || {};
    return {
      ...session,
      gex_curve: c.gex_curve || session.gex_curve,
      dex_curve: c.dex_curve || session.dex_curve,
      vex_curve: c.vex_curve || session.vex_curve,
      cex_curve: c.cex_curve || session.cex_curve,
    };
  }

  /**
   * Draw projected GEX/DEX/VEX curves anchored at each session's globex open.
   * Uses the same session classification as the OI distribution curves.
   */
  _drawOiCurves(ctx, timeScale, hPixRatio, vPixRatio, bitmapWidth) {
    if (!this.oiCurvesData) return;

    const showForwardOi = this.options.showForwardOi;

    // Match heatmap/bar colors: GEX blue/orange, DEX red/green, VEX purple/yellow, CEX cyan/rose
    const CURVE_COLORS = {
      gex: { positive: 'rgba(59, 130, 246',  negative: 'rgba(249, 115, 22' },
      dex: { positive: 'rgba(239, 68, 68',   negative: 'rgba(34, 197, 94' },
      vex: { positive: 'rgba(168, 85, 247',  negative: 'rgba(251, 191, 36' },
      cex: { positive: 'rgba(6, 182, 212',   negative: 'rgba(225, 29, 72' },
    };

    const now = Math.floor(Date.now() / 1000);
    const GLOBEX_DURATION = 82800;

    // Sort sessions by globex_open_ts to classify them
    const sessions = Object.values(this.oiCurvesData)
      .filter(s => s.globex_open_ts)
      .sort((a, b) => a.globex_open_ts - b.globex_open_ts);

    const classified = sessions.map(s => {
      const globexClose = s.globex_open_ts + GLOBEX_DURATION;
      const isCompleted = globexClose <= now;
      const isDeveloping = s.globex_open_ts <= now && now < globexClose;
      return { ...s, globexClose, isCompleted, isDeveloping, isFuture: !isCompleted && !isDeveloping };
    });

    const isOptionsHoliday = this.options.isOptionsHoliday;
    let developingIdx = isOptionsHoliday ? -1 : classified.findIndex(s => s.isDeveloping);
    if (developingIdx === -1 && !isOptionsHoliday) {
      developingIdx = classified.findIndex(s => s.isFuture);
    }
    const firstNonCompletedIdx = classified.findIndex(s => !s.isCompleted);
    const t1Idx = developingIdx !== -1 ? developingIdx + 1 : firstNonCompletedIdx;

    for (let i = 0; i < classified.length; i++) {
      const session = classified[i];
      const isDevelopingSession = (i === developingIdx);
      const isT1Session = (i === t1Idx);

      if (isT1Session && !showForwardOi) continue;
      if (!isDevelopingSession && !isT1Session && session.isFuture) continue;

      const globexX = timeScale.timeToCoordinate(session.globex_open_ts);
      const rthX = timeScale.timeToCoordinate(session.rth_open_ts);

      let anchorX;
      let anchorLogical;
      let rightAlign = false;

      if (isT1Session) {
        anchorX = bitmapWidth - 2 * hPixRatio;
        rightAlign = true;
      } else if (isDevelopingSession) {
        // Current session: pin to left edge of visible chart, same as OI bars
        const visRange = timeScale.getVisibleLogicalRange();
        anchorLogical = visRange ? timeScale.logicalToCoordinate(visRange.from) : 0;
        if (anchorLogical === null) anchorLogical = 0;
        anchorX = anchorLogical * hPixRatio;
      } else if (globexX !== null) {
        anchorLogical = globexX;
        anchorX = anchorLogical * hPixRatio;
      } else {
        // Completed session scrolled off-screen - skip to avoid overlapping
        // the developing session which is also pinned to the left edge
        continue;
      }

      // Max curve width
      let maxWidth = this.options.curveMaxWidth || 60;
      if (!rightAlign && rthX !== null && anchorLogical != null && rthX > anchorLogical) {
        const zoneWidth = rthX - anchorLogical;
        maxWidth = Math.min(maxWidth, zoneWidth * 0.9);
      }
      if (maxWidth <= 0) continue;

      // Offset anchors inward so both positive and negative sides are visible
      if (rightAlign) {
        anchorX = bitmapWidth - (maxWidth + 4) * hPixRatio;
      } else if (isDevelopingSession) {
        anchorX += (maxWidth + 4) * hPixRatio;
      }

      // Opacity: completed sessions are fainter
      const strokeAlpha = session.isCompleted ? 0.35 : 0.7;

      // Draw vertical zero line at anchor
      ctx.save();
      ctx.beginPath();
      ctx.setLineDash([4 * vPixRatio, 4 * vPixRatio]);
      ctx.moveTo(anchorX, 0);
      ctx.lineTo(anchorX, ctx.canvas.height);
      ctx.strokeStyle = `rgba(255, 255, 255, ${strokeAlpha * 0.4})`;
      ctx.lineWidth = 1 * Math.min(hPixRatio, vPixRatio);
      ctx.stroke();
      ctx.restore();

      // Draw enabled curve types from the session's curve set (live
      // sub-layer swaps in scrubbed / latest live curves per paint)
      const activeCurves = this._curveSetForSession(session);
      const greekVis = this.options.projectedCurveGreeks || { gex: true, dex: true, vex: true, cex: true };
      const curveKeys = ['gex', 'dex', 'vex', 'cex'].filter(k => greekVis[k] !== false);

      for (const key of curveKeys) {
        const curveData = activeCurves[`${key}_curve`];
        if (!curveData || typeof curveData !== 'object') continue;

        const colors = CURVE_COLORS[key];

        // Build coordinate array from curve data (keys are ES prices)
        const entries = Object.entries(curveData);
        if (entries.length < 2) continue;

        // Find max absolute value for normalization
        let maxAbs = 0;
        for (const [, val] of entries) {
          const absVal = Math.abs(val);
          if (absVal > maxAbs) maxAbs = absVal;
        }
        if (maxAbs === 0) continue;

        // Build coords: keys are already ES prices (hypothetical spots), plot directly
        const coords = [];
        for (const [priceStr, val] of entries) {
          const esPrice = parseFloat(priceStr);
          const y = this.series.priceToCoordinate(esPrice);
          if (y === null) continue;
          const bitmapY = y * vPixRatio;
          const normalized = val / maxAbs;
          // anchorX is the zero line; curve extends left/right from it
          const x = rightAlign
            ? anchorX - normalized * maxWidth * hPixRatio
            : anchorX + normalized * maxWidth * hPixRatio;
          coords.push({ x, y: bitmapY, normalized });
        }
        if (coords.length < 2) continue;

        // Sort by Y (top to bottom = high price to low price)
        coords.sort((a, b) => a.y - b.y);

        // Stroke curve with per-segment coloring based on sign.
        // When a segment crosses zero, split it at the interpolated crossing
        // so the color change aligns with the OI Level zero-crossing lines.
        ctx.save();
        const strokeWidth = session.isCompleted ? 2.5 : 1.5;
        ctx.lineWidth = strokeWidth * Math.min(hPixRatio, vPixRatio);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        for (let j = 1; j < coords.length; j++) {
          const prev = coords[j - 1];
          const curr = coords[j];
          const prevPos = prev.normalized >= 0;
          const currPos = curr.normalized >= 0;

          if (prevPos !== currPos && prev.normalized !== 0 && curr.normalized !== 0) {
            const t = prev.normalized / (prev.normalized - curr.normalized);
            const zeroX = prev.x + t * (curr.x - prev.x);
            const zeroY = prev.y + t * (curr.y - prev.y);

            ctx.beginPath();
            ctx.moveTo(prev.x, prev.y);
            ctx.lineTo(zeroX, zeroY);
            ctx.strokeStyle = `${prevPos ? colors.positive : colors.negative}, ${strokeAlpha})`;
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(zeroX, zeroY);
            ctx.lineTo(curr.x, curr.y);
            ctx.strokeStyle = `${currPos ? colors.positive : colors.negative}, ${strokeAlpha})`;
            ctx.stroke();
          } else {
            ctx.beginPath();
            ctx.moveTo(prev.x, prev.y);
            ctx.lineTo(curr.x, curr.y);
            ctx.strokeStyle = `${currPos ? colors.positive : colors.negative}, ${strokeAlpha})`;
            ctx.stroke();
          }
        }
        ctx.restore();

        // Draw dot at crosshair price intersection
        if (this.crosshairRef?.price != null && coords.length >= 2) {
          const chY = this.series.priceToCoordinate(this.crosshairRef.price);
          if (chY !== null) {
            const chBitmapY = chY * vPixRatio;
            // Interpolate X from coords (sorted by Y top-to-bottom)
            let dotX = null;
            let dotNorm = 0;
            for (let j = 0; j < coords.length - 1; j++) {
              const y0 = coords[j].y, y1 = coords[j + 1].y;
              if ((chBitmapY >= y0 && chBitmapY <= y1) || (chBitmapY >= y1 && chBitmapY <= y0)) {
                const t = (y1 - y0) !== 0 ? (chBitmapY - y0) / (y1 - y0) : 0;
                dotX = coords[j].x + t * (coords[j + 1].x - coords[j].x);
                dotNorm = coords[j].normalized + t * (coords[j + 1].normalized - coords[j].normalized);
                break;
              }
            }
            if (dotX !== null) {
              const dotColor = dotNorm >= 0
                ? `${colors.positive}, 1.0)`
                : `${colors.negative}, 1.0)`;
              const r = 4 * Math.min(hPixRatio, vPixRatio);
              ctx.save();
              ctx.beginPath();
              ctx.arc(dotX, chBitmapY, r, 0, Math.PI * 2);
              ctx.fillStyle = dotColor;
              ctx.fill();
              ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
              ctx.lineWidth = 1;
              ctx.stroke();
              ctx.restore();
            }
          }
        }
      }
    }
  }

  draw(target) {
    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context;
      const timeScale = this.chart.timeScale();
      const horizontalPixelRatio = scope.horizontalPixelRatio || 1;
      const verticalPixelRatio = scope.verticalPixelRatio || 1;

      // Reset drag-anchor hit geometry every paint; only layers that draw a
      // screen-fixed axis below re-record theirs. Guarantees a toggled-off
      // layer, heatmap mode, renko, or a historic-day target never leaves a
      // phantom grab target.
      if (this.anchorHitRef) {
        this.anchorHitRef.today = null;
        this.anchorHitRef.t1 = null;
        this.anchorHitRef.hedge = null;
        this.anchorHitRef.oi = null;
        this.anchorHitRef.smile = null;
        this.anchorHitRef.vol = null;
        this.anchorHitRef.volcum = null;
      }
      // The Exposure Profile owns the "T" chip; Hedging Demand draws its own
      // "HD" chip on its own axis, except when both are candle-anchored at
      // the same historic candle (then the "T" chip alone marks the spot).
      this._todayLabelDrawn = false;

      ctx.save();

      // Draw projected curves (lowest z-order, Globex zone)
      if (this.options.showProjectedCurves && this.oiCurvesData) {
        this._drawOiCurves(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio, scope.bitmapSize.width);
      }

      // Draw OI distribution (two-sided call/put histogram per strike)
      if (this.options.showOiDistribution && this.oiBarsData) {
        this._drawOiDistribution(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio, scope.bitmapSize.width, scope.bitmapSize.height);
      }

      // Hedging Demand: heatmap mode REPLACES the profile bars (one or the
      // other, never both; time by strike matrix of served hedge values)
      if (this.options.showHedgingDemand && this.options.showHedgeDemandHeatmap && this.hedgeRowsData) {
        this._drawHedgingDemandHeatmap(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio);
      }

      // Exposure Profile: heatmap mode REPLACES the profile bars (one or the
      // other, never both; time by strike matrix over the exposure rows)
      if (this.options.showExposureProfile && this.options.showExpProfileHeatmap && this.exposureRowsData) {
        this._drawExposureHeatmap(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio);
      }

      // Draw Exposure Profile bars (per-strike exposure at current spot;
      // latest row by default, crosshair column when hovering a past window;
      // hidden while the heatmap mode is on)
      if (this.options.showExposureProfile && !this.options.showExpProfileHeatmap && this.exposureRowsData) {
        this._drawExposureHistogram(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio, scope.bitmapSize.width, scope.bitmapSize.height);
      }

      // Draw T+1 Exposure Profile (next-session static row pinned right edge;
      // independent of the heatmap mode: it lives in the right-edge zone the
      // heatmap never covers)
      if (this.options.showExposureProfile && this.options.showExpProfileT1 && this.t1RowsData) {
        this._drawT1ExposureProfile(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio, scope.bitmapSize.width, scope.bitmapSize.height);
      }

      // Draw Hedging Demand profile bars (per-strike combined hedge flow at
      // the same target row as the Exposure Profile, on its own draggable
      // "HD" axis; hidden while the heatmap mode is on)
      if (this.options.showHedgingDemand && !this.options.showHedgeDemandHeatmap && this.hedgeRowsData) {
        this._drawHedgeDemandProfile(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio, scope.bitmapSize.width, scope.bitmapSize.height);
      }

      // Draw IV Skew dots (per-strike call/put IVs at the same target row
      // selection, on its own draggable "IV" axis)
      if (this.options.showIvSkew && this.ivSkewData) {
        this._drawIvSkew(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio, scope.bitmapSize.width, scope.bitmapSize.height);
      }

      // Draw Options Volume histograms (per-candle traded volume on the
      // "OV" axis + optional day-cumulative second histogram on the "CV"
      // axis, same crosshair scrubbing as the exposure family)
      if (this.options.showOptionsVolume && this.volumeRowsData) {
        this._drawOptionsVolume(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio, scope.bitmapSize.width, scope.bitmapSize.height);
      }

      // NOTE: Session Level Indicators (PDL, OR, IB) are now drawn by the separate
      // SessionLevelPrimitive, which is managed independently and responds to visibility
      // toggles correctly. This avoids duplicate rendering and stale options issues.

      ctx.restore();
    });
  }

  /**
   * Hedging Demand heatmap MODE (showHedgeDemandHeatmap): time by strike
   * matrix of the served hedge values ({price, hedge} levels from
   * /hedging-demand). Green = net dealer buy-to-hedge, red = net dealer
   * sell-to-hedge. Replaces the hedge profile bars while enabled (one
   * view or the other, never both). Each column spans its source window
   * (rows are LAST-derived per window).
   */
  _drawHedgingDemandHeatmap(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio) {
    if (!this.hedgeRowsData) return;

    for (const [dateKey, session] of Object.entries(this.hedgeRowsData)) {
      const candles = session?.candles || [];
      for (let ci = 0; ci < candles.length; ci++) {
        const candle = candles[ci];
        this._drawHedgeDemandColumn(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio,
                                    candle, candles[ci + 1], dateKey);
      }
    }
  }

  _drawHedgeDemandColumn(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio,
                         candle, nextCandle, dateKey) {
    {
      const candleTime = candle.time;
      const levels = candle.levels || [];
      if (levels.length === 0) return;

      // Data-driven band height: a quarter of the STACK_FILL strike-spacing
      // budget, matching a single Exposure Profile heatmap cell (its 4-greek
      // stack fills the budget; one band = budget / 4).
      const minGap = this._minLevelGap(candle);
      const cellHeight = (minGap != null ? minGap / 4 : this.options.cellHeight) * STACK_FILL;

      const logicalX = timeScale.timeToCoordinate(candleTime);
      if (logicalX === null) return;
      const roundTripTime = timeScale.coordinateToTime(logicalX);
      if (roundTripTime === null) return;

      const x = logicalX * horizontalPixelRatio;
      const barSpacing = (timeScale.options().barSpacing || 6) * horizontalPixelRatio;

      // Widen the column to span its source window: rows are fetched at the
      // chart's display tf (1m on non-minute pages). Anchor at the window
      // start and extend to the next row (fallback: window width).
      const windowSec = this.options.exposureRowsTfSec || 60;
      let logicalXEnd = null;
      if (nextCandle?.time != null) {
        logicalXEnd = timeScale.timeToCoordinate(nextCandle.time);
      }
      if (logicalXEnd === null) {
        logicalXEnd = timeScale.timeToCoordinate(candleTime + windowSec);
      }
      const pad = this.options.cellPadding * horizontalPixelRatio;
      let cellWidth;
      let xLeft;
      if (logicalXEnd !== null && logicalXEnd > logicalX) {
        xLeft = x - (barSpacing / 2) + pad;
        cellWidth = (logicalXEnd - logicalX) * horizontalPixelRatio - pad * 2;
      } else {
        xLeft = x - (barSpacing / 2) + pad;
        cellWidth = barSpacing - pad * 2;
      }

      // Realized mode: per-column recompute (each column resolves its own
      // trailing dS/dIV, components from the matching exposure candle;
      // unresolvable columns fall back to per-unit).
      let hedgeVal = (level) => level.hedge || 0;
      if (this.options.hedgeDemandRealized) {
        const inputs = this._resolveRealizedHedgeInputs(candle, dateKey);
        if (inputs) {
          hedgeVal = (level) => this._realizedHedgeVal(level, inputs);
        }
      }

      // Separate pos/neg max for per-column normalization (matches GEX pattern)
      let maxPos = 0, maxNeg = 0;
      for (const level of levels) {
        const v = hedgeVal(level);
        if (v > maxPos) maxPos = v;
        if (v < maxNeg) maxNeg = v;
      }
      const absNeg = -maxNeg;
      if (maxPos < 1e-9 && absNeg < 1e-9) return;

      for (const level of levels) {
        const price = level.price;
        const esContracts = hedgeVal(level);

        if (Math.abs(esContracts) < 1e-9) continue;

        const logicalYTop = this.series.priceToCoordinate(price + cellHeight / 2);
        const logicalYBottom = this.series.priceToCoordinate(price - cellHeight / 2);
        if (logicalYTop === null || logicalYBottom === null) continue;

        const yTop = logicalYTop * verticalPixelRatio;
        const yBottom = logicalYBottom * verticalPixelRatio;
        const pixelHeight = Math.abs(yBottom - yTop);
        const y = Math.min(yTop, yBottom);
        if (pixelHeight < 1 || cellWidth < 1) continue;

        const vR = esContracts > 0 ? (maxPos > 0 ? esContracts / maxPos : 0)
                 : esContracts < 0 ? (absNeg > 0 ? -esContracts / absNeg : 0)
                 : 0;
        const intensity = Math.pow(vR, 0.7);

        // Dark→bright shade ramp (matches Greek heatmap pattern)
        let darkRgb, brightRgb;
        if (esContracts > 0) {
          darkRgb = [10, 75, 30]; brightRgb = [60, 220, 120];   // green = net dealer buy-to-hedge
        } else {
          darkRgb = [110, 20, 20]; brightRgb = [255, 95, 95];   // red = net dealer sell-to-hedge
        }
        const rgb = darkRgb.map((d, i) => Math.round(d + (brightRgb[i] - d) * intensity));
        const alpha = 0.55 + 0.30 * intensity;
        ctx.fillStyle = `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha.toFixed(2)})`;


        const gap = Math.round(1 * verticalPixelRatio);
        ctx.fillRect(
          Math.round(xLeft),
          Math.round(y) + gap,
          Math.round(cellWidth),
          Math.max(Math.round(pixelHeight) - gap * 2, 1)
        );
      }
    }
  }

  /**
   * Minimum positive gap between a candle's level prices (ES points): the
   * effective strike spacing of that row. Order-independent (sorts a copy;
   * REST levels arrive sorted, SSE levels follow the baseline strike-array
   * order which is not a contract). Memoized on the candle: a row's levels
   * never change after arrival, so the sort runs once per row, not per frame.
   * Returns null with fewer than 2 levels.
   */
  _minLevelGap(candle) {
    if (candle._minGap !== undefined) return candle._minGap;
    const levels = candle.levels || [];
    let minGap = null;
    if (levels.length >= 2) {
      const prices = levels.map(l => l.price).sort((a, b) => a - b);
      for (let i = 1; i < prices.length; i++) {
        const gap = prices[i] - prices[i - 1];
        if (gap > 0 && (minGap === null || gap < minGap)) minGap = gap;
      }
    }
    candle._minGap = minGap;
    return minGap;
  }

  /**
   * Exposure Profile heatmap: time by strike matrix over the shared exposure
   * rows. Each column spans its source window; each enabled greek draws its
   * own cell band, stacked and centered on the strike (GEX top, CEX bottom).
   * Weighting follows exposureProfileMode ('dealer' fields, 'absolute'
   * pure-OI u_* fields). Cells are normalized per column (split-sign max
   * within each candle), so every time slice shows its own strike structure.
   * Descriptive map of exposure magnitude over time, not a direction call.
   */
  _drawExposureHeatmap(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio) {
    const sessions = this.exposureRowsData;
    if (!sessions) return;

    const absolute = (this.options.exposureProfileMode || 'dealer') === 'absolute';
    const toggles = this.options.expProfileToggles || {};
    const greeks = [
      { key: 'gex', field: absolute ? 'u_gex' : 'gex', on: toggles.gex !== false, color: (i) => this._getColor(i) },
      { key: 'dex', field: absolute ? 'u_dex' : 'dex', on: toggles.dex !== false, color: (i) => this._getColorDex(i) },
      { key: 'vex', field: absolute ? 'u_vex' : 'vex', on: toggles.vex !== false, color: (i) => this._getColorVex(i) },
      { key: 'cex', field: absolute ? 'u_cex' : 'cex', on: toggles.cex !== false, color: (i) => this._getColorCex(i) },
    ].filter(g => g.on)
      .map((g, idx) => ({ ...g, stackIdx: idx }))  // canonical stack slot: GEX top -> CEX bottom
      .reverse();  // draw CEX first, GEX last (top layer), legacy z-order
    if (greeks.length === 0) return;

    for (const session of Object.values(sessions)) {
      const candles = session?.candles || [];
      for (let ci = 0; ci < candles.length; ci++) {
        this._drawExposureHeatmapColumn(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio,
                                        candles[ci], candles[ci + 1], greeks);
      }
    }
  }

  _drawExposureHeatmapColumn(ctx, timeScale, horizontalPixelRatio, verticalPixelRatio,
                             candle, nextCandle, greeks) {
    const candleTime = candle.time;
    const levels = candle.levels || [];
    if (levels.length === 0) return;

    const logicalX = timeScale.timeToCoordinate(candleTime);
    if (logicalX === null) return;
    const roundTripTime = timeScale.coordinateToTime(logicalX);
    if (roundTripTime === null) return;

    const x = logicalX * horizontalPixelRatio;
    const barSpacing = (timeScale.options().barSpacing || 6) * horizontalPixelRatio;

    // Widen the column to span its source window (same anchoring as the
    // hedging demand heatmap: window start to the next row, fallback width).
    const windowSec = this.options.exposureRowsTfSec || 60;
    let logicalXEnd = null;
    if (nextCandle?.time != null) {
      logicalXEnd = timeScale.timeToCoordinate(nextCandle.time);
    }
    if (logicalXEnd === null) {
      logicalXEnd = timeScale.timeToCoordinate(candleTime + windowSec);
    }
    const pad = this.options.cellPadding * horizontalPixelRatio;
    const xLeft = x - (barSpacing / 2) + pad;
    let cellWidth;
    if (logicalXEnd !== null && logicalXEnd > logicalX) {
      cellWidth = (logicalXEnd - logicalX) * horizontalPixelRatio - pad * 2;
    } else {
      cellWidth = barSpacing - pad * 2;
    }
    if (cellWidth < 1) return;

    // Per-greek split-sign maxes over this column's own levels.
    const maxes = {};
    for (const g of greeks) {
      const mm = { pos: 0, neg: 0 };
      for (const level of levels) {
        const v = level[g.field] || 0;
        if (v > mm.pos) mm.pos = v;
        if (v < -mm.neg) mm.neg = -v;
      }
      maxes[g.key] = mm;
    }

    // Data-driven stack sizing: a full 4-greek stack fills STACK_FILL of the
    // row's own strike spacing, so adjacent strikes always keep an empty
    // gutter regardless of underlying (SPX 5pt, SPY ~10pt). Each band is a
    // FIXED quarter of that budget: disabling greeks shrinks the stack, it
    // never inflates the remaining bands.
    const minGap = this._minLevelGap(candle);
    const stackBudget = (minGap != null ? minGap : this.options.cellHeight * 4) * STACK_FILL;
    const cellH = stackBudget / 4;
    const stackHeight = cellH * greeks.length;

    const gap = Math.round(1 * verticalPixelRatio);
    for (const g of greeks) {
      const mm = maxes[g.key];
      if (mm.pos < 1e-12 && mm.neg < 1e-12) continue;
      // Center the enabled stack on the strike: GEX top -> CEX bottom.
      const topOffset = stackHeight / 2 - g.stackIdx * cellH;

      for (const level of levels) {
        const v = level[g.field] || 0;
        if (v === 0) continue;

        const vR = v > 0 ? (mm.pos > 0 ? v / mm.pos : 0)
                 : (mm.neg > 0 ? -v / mm.neg : 0);
        const intensity = (v >= 0 ? 1 : -1) * Math.pow(vR, ROLLOFF);
        if (Math.abs(intensity) < 0.001) continue;

        const logicalYTop = this.series.priceToCoordinate(level.price + topOffset);
        const logicalYBottom = this.series.priceToCoordinate(level.price + topOffset - cellH);
        if (logicalYTop === null || logicalYBottom === null) continue;

        const yTop = logicalYTop * verticalPixelRatio;
        const yBottom = logicalYBottom * verticalPixelRatio;
        const pixelHeight = Math.abs(yBottom - yTop);
        const y = Math.min(yTop, yBottom);
        if (pixelHeight < 1) continue;

        ctx.fillStyle = g.color(intensity);
        ctx.fillRect(
          Math.round(xLeft),
          Math.round(y) + gap,
          Math.round(cellWidth),
          Math.max(Math.round(pixelHeight) - gap * 2, 1)
        );
      }
    }
  }

}

/**
 * Primitive view wrapper
 */
class GreeksHeatmapPrimitiveView {
  constructor(series, chart, options) {
    this._renderer = new GreeksHeatmapRenderer(series, chart, options);
  }

  renderer() {
    return this._renderer;
  }

  zOrder() {
    return 'bottom';
  }
}

/**
 * Primitive class for lightweight-charts attachment
 */
class GreeksHeatmapPrimitive {
  constructor(series, chart, options) {
    this._series = series;
    this._chart = chart;
    this._options = options;
    this._paneViews = [new GreeksHeatmapPrimitiveView(series, chart, options)];
  }

  paneViews() {
    return this._paneViews;
  }

  attached(param) {
    this._series = param.series;
    this._requestUpdate = param.requestUpdate;
  }

  detached() {
    this._requestUpdate = undefined;
  }

  requestUpdate() {
    // Signal the chart to redraw - essential for real-time updates
    if (this._requestUpdate) {
      this._requestUpdate();
    } else {
      console.warn('[GreeksHeatmapPrimitive] requestUpdate called but no callback available!');
    }
  }
}

/**
 * Session Level Primitive - draws PDL/OR/IB horizontal lines
 * Separate from GEX heatmap so it can render independently
 */
class SessionLevelPrimitive {
  constructor(series, chart, levelData, options) {
    this.series = series;
    this.chart = chart;
    this.levelData = levelData;
    this.options = options;
    this._renderer = new SessionLevelRenderer(series, chart, levelData, options);
  }

  updateAllViews() {
    this._renderer.update(this.levelData, this.options);
  }

  priceAxisViews() {
    return [];
  }

  timeAxisViews() {
    return [];
  }

  paneViews() {
    return [new SessionLevelView(this._renderer)];
  }

  attached(param) {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
  }

  detached() {
    this._series = null;
    this._chart = null;
    this._requestUpdate = undefined;
  }

  requestUpdate() {
    // Repaint on demand (crosshair scrubbing of the curve-derived OI levels)
    if (this._requestUpdate) {
      this._requestUpdate();
    }
  }
}

/**
 * Session Level View - wraps renderer for lightweight-charts
 */
class SessionLevelView {
  constructor(renderer) {
    this._renderer = renderer;
  }

  zOrder() {
    return 'top';
  }

  renderer() {
    return {
      draw: (target) => this._renderer.draw(target),
      drawBackground: () => {},
    };
  }
}

/**
 * Session Level Renderer - canvas drawing for session levels
 */
class SessionLevelRenderer {
  constructor(series, chart, levelData, options) {
    this.series = series;
    this.chart = chart;
    this.levelData = levelData;
    this.options = options;
  }

  update(levelData, options) {
    this.levelData = levelData;
    this.options = options;
  }

  draw(target) {
    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context;
      const horizontalPixelRatio = scope.horizontalPixelRatio || 1;
      const verticalPixelRatio = scope.verticalPixelRatio || 1;
      const bitmapWidth = scope.bitmapSize.width;

      ctx.save();

      // Get the time scale and series data for coordinate calculations
      const timeScale = this.chart.timeScale();

      // Get the actual data timestamps from the series for interpolation
      const seriesData = this.series.data();
      const dataTimestamps = seriesData.map(d => d.time);

      // Helper to convert timestamp to x coordinate with interpolation for non-exact matches
      // Returns: { x: number, clipped: 'none' | 'left' | 'right' | 'skip' }
      const timeToX = (timestamp, isStartBoundary) => {
        // First, try direct lookup
        const coord = timeScale.timeToCoordinate(timestamp);
        if (coord !== null) {
          return { x: Math.round(coord * horizontalPixelRatio), clipped: 'none' };
        }

        // Timestamp doesn't match an exact data point - use interpolation
        if (dataTimestamps.length === 0) {
          return { x: isStartBoundary ? 0 : bitmapWidth, clipped: 'fallback' };
        }

        // Check if timestamp is before all data
        if (timestamp < dataTimestamps[0]) {
          if (isStartBoundary) {
            // Start is before data - clip to first candle
            const firstCoord = timeScale.timeToCoordinate(dataTimestamps[0]);
            return { x: firstCoord !== null ? Math.round(firstCoord * horizontalPixelRatio) : 0, clipped: 'left' };
          } else {
            // End is before all data - skip line
            return { x: 0, clipped: 'skip' };
          }
        }

        // Check if timestamp is after all data
        if (timestamp > dataTimestamps[dataTimestamps.length - 1]) {
          // Clip to last candle (both start and end boundaries extend from last candle)
          const lastCoord = timeScale.timeToCoordinate(dataTimestamps[dataTimestamps.length - 1]);
          return { x: lastCoord !== null ? Math.round(lastCoord * horizontalPixelRatio) : bitmapWidth, clipped: 'right' };
        }

        // Timestamp is within data range - find bracketing candles and interpolate
        let lowIdx = 0;
        let highIdx = dataTimestamps.length - 1;

        // Binary search for the bracketing timestamps
        while (lowIdx < highIdx - 1) {
          const midIdx = Math.floor((lowIdx + highIdx) / 2);
          if (dataTimestamps[midIdx] <= timestamp) {
            lowIdx = midIdx;
          } else {
            highIdx = midIdx;
          }
        }

        // lowIdx and highIdx now bracket our timestamp
        const lowTime = dataTimestamps[lowIdx];
        const highTime = dataTimestamps[highIdx];

        const lowCoord = timeScale.timeToCoordinate(lowTime);
        const highCoord = timeScale.timeToCoordinate(highTime);

        if (lowCoord !== null && highCoord !== null) {
          // Linear interpolation between the two known coordinates
          const timeFraction = (timestamp - lowTime) / (highTime - lowTime);
          const interpolatedCoord = lowCoord + (highCoord - lowCoord) * timeFraction;
          return { x: Math.round(interpolatedCoord * horizontalPixelRatio), clipped: 'none' };
        }

        // Fall back to nearest candle
        const nearestTime = (timestamp - lowTime) < (highTime - timestamp) ? lowTime : highTime;
        const nearestCoord = timeScale.timeToCoordinate(nearestTime);
        return { x: nearestCoord !== null ? Math.round(nearestCoord * horizontalPixelRatio) : (isStartBoundary ? 0 : bitmapWidth), clipped: 'interpolated' };
      };

      // Get visible price range for calculating Y coordinates even when price is outside visible range
      const bitmapHeight = scope.bitmapSize.height;

      // Helper to convert price to Y coordinate, even for out-of-range prices
      const priceToY = (price) => {
        // First try the standard method
        const y = this.series.priceToCoordinate(price);
        if (y !== null) {
          return Math.round(y * verticalPixelRatio);
        }

        // Price is outside visible range - calculate using linear interpolation
        // Get two known prices from the series data to establish the scale
        const seriesData = this.series.data();
        if (seriesData.length < 2) return null;

        // Find min/max prices from visible candles to establish coordinate mapping
        let minPrice = Infinity, maxPrice = -Infinity;
        let minY = null, maxY = null;

        for (const candle of seriesData) {
          const candleY = this.series.priceToCoordinate(candle.low);
          if (candleY !== null) {
            if (candle.low < minPrice) {
              minPrice = candle.low;
              minY = candleY;
            }
          }
          const highY = this.series.priceToCoordinate(candle.high);
          if (highY !== null) {
            if (candle.high > maxPrice) {
              maxPrice = candle.high;
              maxY = highY;
            }
          }
        }

        if (minY === null || maxY === null || minPrice === maxPrice) return null;

        // Calculate Y using linear interpolation/extrapolation
        // Note: Y increases downward, so higher price = lower Y
        const priceRange = maxPrice - minPrice;
        const yRange = minY - maxY;  // minY is at bottom (higher Y value), maxY is at top
        const priceFraction = (price - minPrice) / priceRange;
        const interpolatedY = minY - (priceFraction * yRange);

        return Math.round(interpolatedY * verticalPixelRatio);
      };

      // Helper to draw a horizontal line at a price level with optional time bounds
      const drawLine = (price, color, isDashed = false, label = '', startTime = null, endTime = null, lineWidth = 1.5, dashPattern = null) => {
        if (price === null || price === undefined) return;

        const yScaled = priceToY(price);
        if (yScaled === null) {
          return;
        }

        // Skip if line would be far outside visible area (more than 2x height away)
        if (yScaled < -bitmapHeight || yScaled > bitmapHeight * 2) {
          return;
        }

        // Calculate x coordinates from time bounds
        let xStart = 0;
        let xEnd = bitmapWidth;

        if (startTime !== null) {
          const result = timeToX(startTime, true);
          if (result.clipped === 'skip') {
            // Start is after visible range - skip line
            return;
          }
          xStart = Math.max(0, result.x);
        }

        if (endTime !== null) {
          const result = timeToX(endTime, false);
          if (result.clipped === 'skip') {
            // End is before visible range - skip line
            return;
          }
          xEnd = Math.min(bitmapWidth, result.x);
        }

        // Skip if line would be outside visible area
        if (xStart >= bitmapWidth || xEnd <= 0 || xStart >= xEnd) {
          return;
        }
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth * horizontalPixelRatio;

        if (dashPattern) {
          ctx.setLineDash(dashPattern.map(v => v * horizontalPixelRatio));
        } else if (isDashed) {
          ctx.setLineDash([6 * horizontalPixelRatio, 4 * horizontalPixelRatio]);
        } else {
          ctx.setLineDash([]);
        }

        ctx.beginPath();
        ctx.moveTo(xStart, yScaled);
        ctx.lineTo(xEnd, yScaled);
        ctx.stroke();

        // Draw label at end of line (right side within the line bounds)
        if (label) {
          const fontSize = Math.round(12 * horizontalPixelRatio);
          ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';

          const labelText = `${label} ${price.toFixed(2)}`;
          const textWidth = ctx.measureText(labelText).width;
          const padding = 4 * horizontalPixelRatio;

          // Position label at the end of the line
          const labelX = xEnd - padding * 2;

          // Only draw label if there's space
          if (labelX > xStart + textWidth + padding * 4) {
            // Background for label
            ctx.fillStyle = 'rgba(15, 20, 25, 0.85)';
            ctx.fillRect(
              labelX - textWidth - padding,
              yScaled - fontSize / 2 - padding,
              textWidth + padding * 2,
              fontSize + padding * 2
            );

            ctx.fillStyle = color;
            ctx.fillText(labelText, labelX, yScaled);
          }
        }
      };

      // Multi-day rendering with time boundaries (keyed by session date)
      if (this.levelData?.levelsByDay && Object.keys(this.levelData.levelsByDay).length > 0) {
        const levelsByDay = this.levelData.levelsByDay;

        for (const [dateStr, dayLevels] of Object.entries(levelsByDay)) {
          // Previous Day Levels (Orange)
          if (this.options.showPrevDayLevels && dayLevels?.prior_levels?.prev_day) {
            const pd = dayLevels.prior_levels.prev_day;
            const color = 'rgba(245, 158, 11, 0.9)';  // Orange
            drawLine(pd.high, color, false, 'PDH', pd.start_time, pd.end_time);
            drawLine(pd.low, color, false, 'PDL', pd.start_time, pd.end_time);
            drawLine(pd.close, color, true, 'PDC', pd.start_time, pd.end_time);
          }

          // Prior Week Levels (Indigo, heavier)
          if (this.options.showPriorWeekLevels && dayLevels?.prior_levels?.prior_week) {
            const pw = dayLevels.prior_levels.prior_week;
            const color = 'rgba(99, 102, 241, 0.95)';  // Indigo
            drawLine(pw.high, color, false, 'PWH', pw.start_time, pw.end_time, 2);
            drawLine(pw.low, color, false, 'PWL', pw.start_time, pw.end_time, 2);
            drawLine(pw.close, color, true, 'PWC', pw.start_time, pw.end_time, 2);
            drawLine(pw.open, color, false, 'PWO', pw.start_time, pw.end_time, 2, [2, 3]);
          }

          // Prior Month Levels (Teal, heaviest)
          if (this.options.showPriorMonthLevels && dayLevels?.prior_levels?.prior_month) {
            const pm = dayLevels.prior_levels.prior_month;
            const color = 'rgba(45, 212, 191, 0.95)';  // Teal
            drawLine(pm.high, color, false, 'PMH', pm.start_time, pm.end_time, 2.5);
            drawLine(pm.low, color, false, 'PML', pm.start_time, pm.end_time, 2.5);
            drawLine(pm.close, color, true, 'PMC', pm.start_time, pm.end_time, 2.5);
            drawLine(pm.open, color, false, 'PMO', pm.start_time, pm.end_time, 2.5, [2, 3]);
          }

          // Asia Session High/Low (Sky Blue)
          if (this.options.showAsiaLevels && dayLevels?.overnight_sessions?.asia) {
            const as = dayLevels.overnight_sessions.asia;
            const color = 'rgba(56, 189, 248, 0.9)';  // Sky blue
            drawLine(as.high, color, false, 'ASH', as.start_time, as.end_time);
            drawLine(as.low, color, false, 'ASL', as.start_time, as.end_time);
          }

          // London Session High/Low (Lime)
          if (this.options.showLondonLevels && dayLevels?.overnight_sessions?.london) {
            const ld = dayLevels.overnight_sessions.london;
            const color = 'rgba(163, 230, 53, 0.9)';  // Lime
            drawLine(ld.high, color, false, 'LDH', ld.start_time, ld.end_time);
            drawLine(ld.low, color, false, 'LDL', ld.start_time, ld.end_time);
          }

          // Opening Range (Purple)
          if (this.options.showOpeningRange && dayLevels?.opening_range) {
            const or = dayLevels.opening_range;
            const color = 'rgba(139, 92, 246, 0.9)';  // Purple
            drawLine(or.high, color, false, 'ORH', or.start_time, or.end_time);
            drawLine(or.low, color, false, 'ORL', or.start_time, or.end_time);
            drawLine(or.mid, color, true, 'ORM', or.start_time, or.end_time);
          }

          // Initial Balance (Cyan)
          if (this.options.showInitialBalance && dayLevels?.initial_balance) {
            const ib = dayLevels.initial_balance;
            const color = 'rgba(6, 182, 212, 0.9)';  // Cyan
            drawLine(ib.high, color, false, 'IBH', ib.start_time, ib.end_time);
            drawLine(ib.low, color, false, 'IBL', ib.start_time, ib.end_time);
            drawLine(ib.mid, color, true, 'IBM', ib.start_time, ib.end_time);
          }

          // Opening Prints (Green - single price levels)
          if (this.options.showOpeningPrints && dayLevels?.opening_prints) {
            const op = dayLevels.opening_prints;
            const gopColor = 'rgba(34, 197, 94, 0.9)';     // Green
            const rthopColor = 'rgba(16, 185, 129, 0.9)';  // Emerald
            if (op.globex_open != null) {
              drawLine(op.globex_open, gopColor, false, 'GOP', op.start_time, op.end_time);
            }
            if (op.rth_open != null) {
              drawLine(op.rth_open, rthopColor, false, 'RTHOP', op.rth_open_time, op.end_time);
            }
          }

          // Pivot Points (Gold/Red/Blue)
          if (this.options.showPivotPoints && dayLevels?.pivot_points) {
            const pv = dayLevels.pivot_points;
            const ppColor = 'rgba(251, 191, 36, 0.9)';   // Gold
            const rColor = 'rgba(239, 68, 68, 0.7)';     // Red (resistance)
            const sColor = 'rgba(59, 130, 246, 0.7)';    // Blue (support)
            drawLine(pv.pp, ppColor, false, 'PP', pv.start_time, pv.end_time);
            drawLine(pv.r1, rColor, false, 'R1', pv.start_time, pv.end_time);
            drawLine(pv.r2, rColor, true, 'R2', pv.start_time, pv.end_time);
            drawLine(pv.r3, rColor, true, 'R3', pv.start_time, pv.end_time);
            drawLine(pv.s1, sColor, false, 'S1', pv.start_time, pv.end_time);
            drawLine(pv.s2, sColor, true, 'S2', pv.start_time, pv.end_time);
            drawLine(pv.s3, sColor, true, 'S3', pv.start_time, pv.end_time);
          }

          // Zero Levels (projected-curve zero crossings). Static per
          // session: the zero-crossing levels come from the session bake
          // (fixed expiry-minus-24h reference, full unfiltered lists) and
          // never move with the crosshair.
          if (this.options.showZeroLevels && dayLevels?.zero_levels) {
            const zl = dayLevels.zero_levels;

            // Collect all zero levels for grouped label rendering
            const zeroLevels = [];

            // Projected curve zero crossings (gamma flip, delta neutral,
            // vanna trigger): per-greek arrays of {price, below, above}
            // objects; below/above are read but not rendered
            // (descriptive-only rule, they serve programmatic consumers).
            const oiGreekVis = this.options.zeroLevelGreeks || { gex: true, dex: true, vex: true, cex: true };
            const levelConfig = {
              gex: { color: 'rgba(0, 255, 200, 0.9)',  label: 'γ0' },
              dex: { color: 'rgba(59, 130, 246, 0.9)', label: 'δ0' },
              vex: { color: 'rgba(168, 85, 247, 0.9)', label: 'ν0' },
              cex: { color: 'rgba(6, 182, 212, 0.9)',  label: 'χ0' },
            };
            for (const [greek, config] of Object.entries(levelConfig)) {
              if (!oiGreekVis[greek]) continue;
              for (const level of (zl[greek] || [])) {
                if (level && level.price != null) {
                  zeroLevels.push({ price: level.price, color: config.color, dashed: true, label: config.label });
                }
              }
            }

            // Live Flips sub-layer: per-minute live-book crossings for any
            // day with stored rows (additive; the bake threshold lines
            // above are untouched). Default row = the day's LAST stored
            // minute; hovering a time inside the day's rows scrubs to the
            // state nearest at-or-before the cursor. Solid line = tracked
            // + stabilized flip per greek (label suffix L); faint dashed =
            // that minute's other raw crossings.
            const flipsHist = (this.options.liveFlipsHistory || {})[dateStr];
            if (this.options.showZeroLevelsLive && flipsHist && flipsHist.length) {
              let liveRow = flipsHist[flipsHist.length - 1];
              const ct = this.options.crosshairRef?.time;
              if (ct != null && ct >= flipsHist[0].time
                  && ct <= flipsHist[flipsHist.length - 1].time + 60) {
                for (let i = flipsHist.length - 1; i >= 0; i--) {
                  if (flipsHist[i].time <= ct) { liveRow = flipsHist[i]; break; }
                }
              }
              // Full-mode hybrid: when tail slices ride settled marks
              // (refit.tail_held from the row; absent on pre-deploy rows)
              // the slow greeks' (vanna/charm = vex/cex) flip lines draw
              // faded so a static line reads as "mostly settled marks",
              // not "frozen display".
              const tailHeld = (liveRow.refit?.tail_held || 0) > 0;
              for (const [greek, config] of Object.entries(levelConfig)) {
                if (!oiGreekVis[greek]) continue;
                const flip = (liveRow.flip || {})[greek];
                const faded = config.color.replace('0.9)', '0.35)');
                for (const price of ((liveRow.crossings || {})[greek] || [])) {
                  if (price == null) continue;
                  if (flip != null && Math.abs(price - flip) < 0.5) continue;
                  zeroLevels.push({ price, color: faded, dashed: true, label: config.label + 'L' });
                }
                if (flip != null) {
                  const slow = tailHeld && (config.greek === 'vex' || config.greek === 'cex');
                  zeroLevels.push({ price: flip, color: slow ? faded : config.color, dashed: false, label: config.label + 'L' });
                }
              }
            }

            // Merge labels for levels within a few ES points so the
            // unfiltered crossing lists stay readable; every line still
            // draws individually.
            const GROUPING_THRESHOLD = 3.0;
            const sortedLevels = [...zeroLevels].sort((a, b) => a.price - b.price);
            const groups = [];
            let currentGroup = [];

            for (const level of sortedLevels) {
              if (currentGroup.length === 0) {
                currentGroup.push(level);
              } else {
                const lastPrice = currentGroup[currentGroup.length - 1].price;
                if (level.price - lastPrice <= GROUPING_THRESHOLD) {
                  currentGroup.push(level);
                } else {
                  groups.push(currentGroup);
                  currentGroup = [level];
                }
              }
            }
            if (currentGroup.length > 0) {
              groups.push(currentGroup);
            }

            // Pre-compute x bounds for zero levels
            let oiXStart = 0;
            let oiXEnd = bitmapWidth;
            if (zl.start_time !== null && zl.start_time !== undefined) {
              const result = timeToX(zl.start_time, true);
              if (result.clipped === 'skip') {
                oiXStart = bitmapWidth; // will skip all lines
              } else {
                oiXStart = Math.max(0, result.x);
              }
            }
            if (zl.end_time !== null && zl.end_time !== undefined) {
              const result = timeToX(zl.end_time, false);
              if (result.clipped === 'skip') {
                oiXEnd = 0; // will skip all lines
              } else {
                oiXEnd = Math.min(bitmapWidth, result.x);
              }
            }

            // Draw all lines then grouped labels
            const lineCoords = [];
            for (const level of zeroLevels) {
              if (level.price === null || level.price === undefined) continue;
              const yScaled = priceToY(level.price);
              if (yScaled === null) continue;
              if (yScaled < -bitmapHeight || yScaled > bitmapHeight * 2) continue;
              if (oiXStart >= bitmapWidth || oiXEnd <= 0 || oiXStart >= oiXEnd) continue;

              ctx.strokeStyle = level.color;
              ctx.lineWidth = 2 * horizontalPixelRatio;
              ctx.setLineDash(level.dashed ? [6 * horizontalPixelRatio, 4 * horizontalPixelRatio] : []);
              ctx.beginPath();
              ctx.moveTo(oiXStart, yScaled);
              ctx.lineTo(oiXEnd, yScaled);
              ctx.stroke();

              lineCoords.push({ ...level, xStart: oiXStart, xEnd: oiXEnd, yScaled });
            }

            // Draw grouped labels
            for (const group of groups) {
              if (group.length === 0) continue;

              // Find the line coords for this group
              const groupCoords = group.map(lvl => lineCoords.find(lc => lc.price === lvl.price)).filter(Boolean);
              if (groupCoords.length === 0) continue;

              // Calculate label position (average Y, rightmost X)
              const avgY = groupCoords.reduce((sum, c) => sum + c.yScaled, 0) / groupCoords.length;
              const xEnd = Math.max(...groupCoords.map(c => c.xEnd));

              // Build combined label
              let labelText;
              if (group.length === 1) {
                // Single level - show label and price
                labelText = `${group[0].label} ${group[0].price.toFixed(2)}`;
              } else {
                // Multiple levels - combine labels and show price range
                const labels = group.map(l => l.label).join('/');
                const minPrice = Math.min(...group.map(l => l.price));
                const maxPrice = Math.max(...group.map(l => l.price));
                if (maxPrice - minPrice < 1) {
                  labelText = `${labels} ${minPrice.toFixed(2)}`;
                } else {
                  labelText = `${labels} ${minPrice.toFixed(2)}-${maxPrice.toFixed(2)}`;
                }
              }

              // Use the color of the first level in the group (or blend)
              const labelColor = group[0].color;

              const fontSize = Math.round(12 * horizontalPixelRatio);
              ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
              ctx.textAlign = 'right';
              ctx.textBaseline = 'middle';

              const textWidth = ctx.measureText(labelText).width;
              const padding = 4 * horizontalPixelRatio;
              const labelX = xEnd - padding * 2;

              // Draw background
              ctx.fillStyle = 'rgba(15, 20, 25, 0.85)';
              ctx.fillRect(
                labelX - textWidth - padding,
                avgY - fontSize / 2 - padding,
                textWidth + padding * 2,
                fontSize + padding * 2
              );

              // Draw text
              ctx.fillStyle = labelColor;
              ctx.fillText(labelText, labelX, avgY);
            }
          }

          // Gamma Levels (separate indicator; Tier 1 of
          // docs/gamma-levels-exploration.md). Session-static values from
          // the settlement bake: GVWAP (gamma-weighted average price at
          // the bake spot) with its stdev band, plus the top-3 gamma
          // strikes (GW1-GW3, weight-descending array order). The band
          // hugs the money and the walls re-select each session.
          // Descriptive only.
          if (this.options.showGammaLevels && dayLevels?.gamma_levels) {
            const gl = dayLevels.gamma_levels;
            const gt = this.options.gammaLevelToggles || {};
            const wallColor = 'rgba(180, 83, 9, 0.85)';  // Darker amber

            // Day x-span (same clipping semantics as drawLine)
            let glXStart = 0;
            let glXEnd = bitmapWidth;
            let glSkip = false;
            if (gl.start_time != null) {
              const r = timeToX(gl.start_time, true);
              if (r.clipped === 'skip') glSkip = true; else glXStart = Math.max(0, r.x);
            }
            if (!glSkip && gl.end_time != null) {
              const r = timeToX(gl.end_time, false);
              if (r.clipped === 'skip') glSkip = true; else glXEnd = Math.min(bitmapWidth, r.x);
            }
            if (glSkip || glXStart >= glXEnd) {
              // Day span outside the visible range: draw nothing
            } else {
              // Band first so the lines draw above the translucent fill.
              // Soft zone, no edge strokes.
              if (gt.band !== false && gl.gvwap?.band && gl.gvwap.band.lower != null && gl.gvwap.band.upper != null) {
                const yLo = priceToY(gl.gvwap.band.lower);
                const yHi = priceToY(gl.gvwap.band.upper);
                if (yLo !== null && yHi !== null) {
                  ctx.fillStyle = 'rgba(217, 119, 6, 0.08)';
                  ctx.fillRect(glXStart, Math.min(yLo, yHi), glXEnd - glXStart, Math.abs(yHi - yLo));
                }
              }

              // GVWAP line
              if (gt.gvwap !== false && gl.gvwap?.price != null) {
                drawLine(gl.gvwap.price, 'rgba(217, 119, 6, 0.95)', false, 'GVWAP', gl.start_time, gl.end_time, 2);
              }

              if (gt.walls !== false && Array.isArray(gl.walls)) {
                gl.walls.forEach((w, i) => {
                  if (w && w.price != null) {
                    drawLine(w.price, wallColor, true, `GW${i + 1}`, gl.start_time, gl.end_time, 1.5);
                  }
                });
              }
            }
          }

          // Expected Move bands (IV-based, 1σ range)
          if (this.options.showExpectedMove && dayLevels?.expected_move) {
            const em = dayLevels.expected_move;
            const emStartTime = em.start_time;
            const emEndTime = em.end_time;

            // Helper to draw EM line with label
            const drawEmLine = (price, color, isDashed, label) => {
              if (price === null || price === undefined) return;
              const yScaled = priceToY(price);
              if (yScaled === null) return;
              if (yScaled < -bitmapHeight || yScaled > bitmapHeight * 2) return;

              let xStart = 0;
              let xEnd = bitmapWidth;
              if (emStartTime !== null && emStartTime !== undefined) {
                const result = timeToX(emStartTime, true);
                if (result.clipped === 'skip') return;
                xStart = Math.max(0, result.x);
              }
              if (emEndTime !== null && emEndTime !== undefined) {
                const result = timeToX(emEndTime, false);
                if (result.clipped === 'skip') return;
                xEnd = Math.min(bitmapWidth, result.x);
              }
              if (xStart >= bitmapWidth || xEnd <= 0 || xStart >= xEnd) return;

              ctx.strokeStyle = color;
              ctx.lineWidth = 1.5 * horizontalPixelRatio;
              ctx.setLineDash(isDashed ? [6 * horizontalPixelRatio, 4 * horizontalPixelRatio] : []);
              ctx.beginPath();
              ctx.moveTo(xStart, yScaled);
              ctx.lineTo(xEnd, yScaled);
              ctx.stroke();

              // Draw label
              const fontSize = Math.round(11 * horizontalPixelRatio);
              ctx.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`;
              ctx.textAlign = 'right';
              ctx.textBaseline = 'middle';
              const labelText = `${label} ${price.toFixed(2)}`;
              const textWidth = ctx.measureText(labelText).width;
              const padding = 3 * horizontalPixelRatio;
              const labelX = xEnd - padding * 2;

              ctx.fillStyle = 'rgba(15, 20, 25, 0.8)';
              ctx.fillRect(
                labelX - textWidth - padding,
                yScaled - fontSize / 2 - padding,
                textWidth + padding * 2,
                fontSize + padding * 2
              );
              ctx.fillStyle = color;
              ctx.fillText(labelText, labelX, yScaled);
            };

            // 0DTE - Yellow, Solid
            if (em.zero_dte) {
              const c = 'rgba(250, 204, 21, 0.85)';
              drawEmLine(em.zero_dte.upper, c, false, 'EM 0D\u2191');
              drawEmLine(em.zero_dte.lower, c, false, 'EM 0D\u2193');
            }
            // Weekly - Green, Dashed
            if (em.weekly) {
              const c = 'rgba(34, 197, 94, 0.7)';
              drawEmLine(em.weekly.upper, c, true, 'EM W\u2191');
              drawEmLine(em.weekly.lower, c, true, 'EM W\u2193');
            }
            // Monthly - Blue, Dashed
            if (em.monthly) {
              const c = 'rgba(96, 165, 250, 0.7)';
              drawEmLine(em.monthly.upper, c, true, 'EM M\u2191');
              drawEmLine(em.monthly.lower, c, true, 'EM M\u2193');
            }

            ctx.setLineDash([]);
          }
        }
      }

      ctx.restore();
    });
  }
}

/**
 * GEX Heatmap Plugin
 * Main interface for chart integration
 */
import { authFetch } from '../auth/authFetch';
import { roundToTick, getOpenSessionDate } from '../utils/market';
import { unifiedSSE } from '../hooks/useUnifiedSSE';

// Backend-valid display timeframes (mirror of TIMEFRAMES in
// calculations/greeks_heatmap.py). The exposure rows are stored on a 1m
// base and LAST-derived server-side to whichever of these is requested;
// anything else (renko, sub-minute) falls back to 1m.
const TF_SECONDS = {
  '1m': 60, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '4h': 14400, '1d': 86400,
};

export class GreeksHeatmapPlugin {
  constructor(chart, series, options = {}) {
    this.chart = chart;
    this.series = series;
    this.options = {
      apiUrl: options.apiUrl || window.location.origin,
      timeframe: options.timeframe || '5m',
      // All indicators off by default on startup
      // Dealer hedging indicators (per-strike net dealer hedge flow)
      showHedgingDemand: options.showHedgingDemand === true,
      // Session level indicators
      showPrevDayLevels: options.showPrevDayLevels === true,   // PDH/PDL/PDC
      showPriorWeekLevels: options.showPriorWeekLevels === true,   // PWH/PWL/PWC/PWO
      showPriorMonthLevels: options.showPriorMonthLevels === true, // PMH/PML/PMC/PMO
      showAsiaLevels: options.showAsiaLevels === true,          // ASH/ASL
      showLondonLevels: options.showLondonLevels === true,      // LDH/LDL
      showOpeningRange: options.showOpeningRange === true,      // OR High/Low/Mid
      showInitialBalance: options.showInitialBalance === true,  // IB High/Low/Mid
      showOpeningPrints: options.showOpeningPrints === true,    // GOP/RTHOP
      showPivotPoints: options.showPivotPoints === true,        // Floor pivots
      showZeroLevels: options.showZeroLevels === true,              // Zero-crossing key levels
      zeroLevelGreeks: options.zeroLevelGreeks || { gex: true, dex: true, vex: true, cex: true },
      showGammaLevels: options.showGammaLevels === true,        // At-spot GVWAP + band + walls
      gammaLevelToggles: options.gammaLevelToggles || { gvwap: true, band: true, walls: true },
      showExpectedMove: options.showExpectedMove === true,      // IV-based expected move bands
      // OI baseline toggles
      showOiDistribution: options.showOiDistribution === true,    // Raw OI distribution per strike
      oiDistToggles: options.oiDistToggles || { callOi: true, putOi: true, net: true },
      showForwardOi: options.showForwardOi === true,            // T+1 session curves (Projected Curves)
      showProjectedCurves: options.showProjectedCurves === true, // Projected curves per session
      projectedCurveGreeks: options.projectedCurveGreeks || { gex: true, dex: true, vex: true, cex: true },
      showZeroLevelsLive: options.showZeroLevelsLive === true,      // Live Flips sub-layer (per-minute live-book crossings)
      projCurvesLive: options.projCurvesLive === true,          // Live sub-layer: today follows the live book + cursor scrub
      expProfileLiveSmile: options.expProfileLiveSmile === true, // Exposure Profile source: per-minute refit smile
      showExposureProfile: options.showExposureProfile === true, // Exposure Profile: per-strike exposure at current spot
      expProfileToggles: options.expProfileToggles || { gex: true, dex: true, vex: true, cex: true },
      exposureProfileMode: options.exposureProfileMode || 'dealer', // 'dealer' (OI x dealer ratio) | 'absolute' (OI grip)
      showExpProfileHeatmap: options.showExpProfileHeatmap === true, // Exposure Profile heatmap sub-layer
      showExpProfileT1: options.showExpProfileT1 === true, // T+1 Exposure Profile sub-layer (right edge)
      showHedgeDemandHeatmap: options.showHedgeDemandHeatmap === true, // Hedging Demand heatmap sub-layer
      hedgeDemandRealized: options.hedgeDemandRealized === true, // Hedging Demand realized mode (actual trailing dS/dIV instead of per-unit)
      showIvSkew: options.showIvSkew === true, // IV Skew: per-strike call/put IV dots (follows expiryMode)
      // IV Skew derived overlay (parent-gated sub-toggle, default ON).
      // FULL mode: the band describes the FRONT expiry (labeled).
      ivSkewBand: options.ivSkewBand !== false,   // risk-neutral 25-75 band + median/mode
      ivSkewShapePane: options.ivSkewShapePane === true, // IV Skew Shape pane consumer (widens the smile fetch gate)
      // Cell sizing: fallback only for single-level columns; heatmap columns
      // self-size from the row's own strike spacing (_minLevelGap).
      underlying: options.underlying || 'spx',
      cellHeight: options.cellHeight || 2.0,
      dexCellHeight: options.dexCellHeight || 2.0,
      vexCellHeight: options.vexCellHeight || 2.0,
      cexCellHeight: options.cexCellHeight || 2.0,
      ...options
    };

    this.primitive = null;
    this.sessionLevelPrimitive = null;  // Separate primitive for session levels
    this.enabled = false;
    this._enableGeneration = 0;        // Monotonic counter to invalidate stale enable() calls
    this.sessionLevelData = null;  // PDL/OR/IB level data from useLevelIndicators
    this._liveFlipsHistory = {};      // Live Flips rows per day (ES-converted), latest = tail
    this._liveCurvesSnapshot = null;  // Latest full live curve set {date, curves} (ES-converted)
    this._liveCurvesHistory = {};     // Cursor-scrub history {date: sorted [{time, curves}]}
    this._sseUnsubscribers = null;
    this.exposureRowsData = null;      // Exposure Profile rows keyed by session date {date: {candles}}
    this.hedgeRowsData = null;         // Hedging Demand rows keyed by session date (levels {price, hedge}, /hedging-demand)
    this.t1RowsData = null;            // T+1 exposure rows per session date (mirrors exposureRowsData shape)
    this.ivSkewData = null;           // IV Skew rows per session date ({date: {candles: [{time, points}]}})
    this.volumeRowsData = null;        // Options Volume cumulative rows per session date (mirrors exposureRowsData shape)
    this._quoteIvFlat = null;          // Flattened time-sorted ATM IV points [{time, value}] for realized Hedging Demand
    this._lastIvDrawTs = 0;            // Throttle for quote-IV-driven redraws
    this.lastFetchParams = null;
    // Series data change handler for redrawing when ES candles arrive
    this._dataChangedHandler = null;
    this._dataChangeRedrawTimeout = null;
    this._ratiosByDate = {};  // Per-day ES/SPX ratios for price normalization
    // (Difference mode EMA state removed)
    this._lastFullDrawTime = 0;
    this._throttledDrawTimer = null;
    this._crosshairRef = { price: null, time: null };  // Shared ref for crosshair price+time (read by renderer)
    this._crosshairHandler = null; // Crosshair subscription handler
    // Shared refs for the drag-to-place profile anchors (same mutable-ref
    // pattern as _crosshairRef so drags repaint via requestUpdate without a
    // full primitive rebuild). Fractions of pane width; null = default pin.
    this._anchorRef = {
      todayFrac: options.expProfileAnchorFrac ?? null,  // Exposure Profile axis (open session)
      t1Frac: options.expProfileT1AnchorFrac ?? null,   // T+1 Exposure Profile axis
      hedgeFrac: options.hedgeDemandAnchorFrac ?? null, // Hedging Demand axis (open session)
      oiFrac: options.oiProfileAnchorFrac ?? null,      // OI distribution axis (developing session)
      smileFrac: options.ivSkewAnchorFrac ?? null,     // IV Skew axis (open session)
      volFrac: options.optVolAnchorFrac ?? null,        // Options Volume per-candle axis (open session)
      volCumFrac: options.optVolCumAnchorFrac ?? null,  // Options Volume cumulative axis (open session)
    };
    this._anchorHitRef = { today: null, t1: null, hedge: null, oi: null, smile: null, vol: null, volcum: null };  // Renderer-written axis geometry (media px) for hit-testing
  }

  /**
   * Fetch the OI-derived overlays this plugin renders (OI distribution bars,
   * projected curves, exposure rows for the Exposure Profile / Hedging Demand)
   * from their dedicated endpoints, based on the enabled toggles.
   */
  async fetchData(date, timeframe = null, endDate = null) {
    try {
      const tf = timeframe || this.options.timeframe;
      const today = endDate || new Date().toISOString().split('T')[0];

      // Determine if viewing live day (end date is today's date)
      const actualToday = new Date().toISOString().split('T')[0];
      const isLiveDay = today === actualToday;

      const startDate = date;

      // Fetch OI bars, OI curves, exposure rows and hedge rows in parallel
      // as needed. Since the 3.5 split, Hedging Demand has its own payload
      // (/hedging-demand); the exposure rows are fetched for the Exposure
      // Profile, or for HD realized mode (component recompute source).
      const fetches = [];
      if (this.options.showOiDistribution) {
        fetches.push(this.fetchOiBars(startDate, today));
      }
      // Zero Levels are static (served by /zero-levels) and no longer need the
      // curve payload; only the curve overlays themselves fetch it.
      if (this.options.showProjectedCurves) {
        fetches.push(this.fetchOiCurves(startDate, today));
      }
      if ((this.options.showZeroLevels && this.options.showZeroLevelsLive)
          || (this.options.showProjectedCurves && this.options.projCurvesLive)) {
        fetches.push(this.fetchLiveCurves(startDate));
      }
      if (this.options.showExposureProfile
          || (this.options.showHedgingDemand && this.options.hedgeDemandRealized
              && this._realizedHedgeSupported())) {
        fetches.push(this.fetchExposureRows(startDate, today));
      }
      if (this.options.showHedgingDemand) {
        fetches.push(this.fetchHedgingDemand(startDate, today));
      }
      if (this.options.showIvSkew) {
        fetches.push(this.fetchIvSkew(startDate, today));
      }
      if (this.options.showOptionsVolume) {
        fetches.push(this.fetchVolumeRows(startDate, today));
      }
      if (fetches.length > 0) {
        await Promise.all(fetches);
      }

      // Store fetch params including isLiveDay for rendering decisions
      this.lastFetchParams = { date, timeframe: tf, endDate: today, isLiveDay };

      return { ok: true };
    } catch (error) {
      console.error('[GreeksHeatmap] Fetch error:', error);
      return null;
    }
  }

  /**
   * Fetch OI histogram bars from the dedicated bars endpoint.
   * Static per-session, doesn't need re-fetch on timeframe changes.
   */
  async fetchOiBars(startDate, endDate) {
    try {
      const expiryMode = this.options.expiryMode || '0dte';
      const underlying = this.options.underlying || 'spx';
      // rv=1: 5.9 meta envelope (metadata -> meta, echo trims)
      const url = `${this.options.apiUrl}/api/greeks/oi-bars?start_date=${startDate}&end_date=${endDate}&expiry_mode=${expiryMode}&underlying=${underlying}&rv=1`;
      let resp = await authFetch(url);
      if (resp.status === 429) {
        console.warn('[GreeksHeatmap] OI bars rate limited, retrying in 3s...');
        await new Promise(r => setTimeout(r, 3000));
        resp = await authFetch(url);
      }
      if (!resp.ok) throw new Error(`OI bars API error: ${resp.status}`);

      const data = await resp.json();
      this.oiBarsData = data.oi_bars || null;
      this._isOptionsHoliday = data.meta?.is_options_holiday || false;

      if (data.meta?.ratios_by_date) {
        this._ratiosByDate = { ...this._ratiosByDate, ...data.meta.ratios_by_date };
      }

      if (this.oiBarsData) {
        const barDates = Object.keys(this.oiBarsData);
        const barCounts = barDates.map(d => this.oiBarsData[d].bars?.length || 0);
        console.log('[GreeksHeatmap] OI bars loaded:', barDates.length, 'sessions, bars per session:', barCounts);
      }
      return data;
    } catch (error) {
      console.error('[GreeksHeatmap] OI bars fetch error:', error);
      return null;
    }
  }

  /**
   * Display timeframe forwarded to the exposure-rows endpoint: the chart
   * timeframe when it is a backend-valid candle tf, else 30m (renko and
   * other non-minute pages have no real candle tf; 1m here pulled a
   * 13MB+/day payload on those pages).
   */
  _windowsTf() {
    const tf = this.options.timeframe;
    return (tf && TF_SECONDS[tf]) ? tf : '30m';
  }

  /** Second width of the display-tf bucket _windowsTf() resolves to. */
  _windowsTfSec() {
    return TF_SECONDS[this._windowsTf()];
  }

  /**
   * Fetch the static session-bake OI projected curves from the dedicated
   * curves endpoint. mergeOnly=true merges the fetched sessions into the
   * existing data instead of replacing it (the 60s today-refresh path).
   * rv is a browser-cache version buster (rv=3: meta envelope + trimmed
   * per-day fields, the 3.6 trim; rv=2 was the metadata era).
   */
  async fetchOiCurves(startDate, endDate, mergeOnly = false) {
    try {
      const expiryMode = this.options.expiryMode || '0dte';
      const underlying = this.options.underlying || 'spx';
      const url = `${this.options.apiUrl}/api/greeks/oi-curves?start_date=${startDate}&end_date=${endDate}&expiry_mode=${expiryMode}&underlying=${underlying}&rv=3`;
      let resp = await authFetch(url);
      if (resp.status === 429) {
        console.warn('[GreeksHeatmap] OI curves rate limited, retrying in 3s...');
        await new Promise(r => setTimeout(r, 3000));
        resp = await authFetch(url);
      }
      if (!resp.ok) throw new Error(`OI curves API error: ${resp.status}`);

      const data = await resp.json();
      if (mergeOnly && this.oiCurvesData && data.oi_curves) {
        this.oiCurvesData = { ...this.oiCurvesData, ...data.oi_curves };
      } else {
        this.oiCurvesData = data.oi_curves || null;
      }
      this._isOptionsHoliday = data.meta?.is_options_holiday || this._isOptionsHoliday || false;

      if (data.meta?.ratios_by_date) {
        this._ratiosByDate = { ...this._ratiosByDate, ...data.meta.ratios_by_date };
      }

      if (this.oiCurvesData) {
        console.log('[GreeksHeatmap] OI curves loaded:', Object.keys(this.oiCurvesData).length, 'sessions');
      }
      return data;
    } catch (error) {
      console.error('[GreeksHeatmap] OI curves fetch error:', error);
      return null;
    }
  }

  /**
   * Curve data handed to the renderer: the bake curves, with each entry
   * carrying its date key, and (when the Live sub-layer is on) today's
   * entry overlaid with the latest live curve set. Timestamps come from
   * the bake entry so session classification and anchoring are
   * unchanged; before today's bake exists there is nothing to overlay.
   */
  _effectiveOiCurvesData() {
    const base = this.oiCurvesData;
    if (!base) return base;
    const withDates = {};
    for (const [d, entry] of Object.entries(base)) {
      withDates[d] = entry.date ? entry : { ...entry, date: d };
    }
    if (this.options.projCurvesLive && this._liveCurvesSnapshot?.curves) {
      const d = this._liveCurvesSnapshot.date;
      if (withDates[d]) {
        const c = this._liveCurvesSnapshot.curves;
        withDates[d] = {
          ...withDates[d],
          gex_curve: c.gex_curve || withDates[d].gex_curve,
          dex_curve: c.dex_curve || withDates[d].dex_curve,
          vex_curve: c.vex_curve || withDates[d].vex_curve,
          cex_curve: c.cex_curve || withDates[d].cex_curve,
        };
      }
    }
    return withDates;
  }

  /**
   * Fetch the live-curves rows (Live Flips scrub history, latest = tail),
   * the current full-curve snapshot, and (when the live Projected Curves
   * sub-layer wants them) the 1-min curve snapshots for cursor scrub.
   * Served ES-converted; any failure leaves state unchanged so the bake
   * displays are never affected.
   */
  async fetchLiveCurves(startDate = null, skipSnaps = false) {
    try {
      const expiryMode = this.options.expiryMode || '0dte';
      const underlying = this.options.underlying || 'spx';
      const today = new Date().toISOString().split('T')[0];
      const start = (startDate && !skipSnaps) ? startDate : today;
      const wantSnaps = this.options.projCurvesLive === true && !skipSnaps;
      const snapParam = wantSnaps ? '&snapshots=1' : '';
      const url = `${this.options.apiUrl}/api/greeks/live-curves?start_date=${start}&end_date=${today}&expiry_mode=${expiryMode}&underlying=${underlying}${snapParam}&rv=2`;
      let resp = await authFetch(url);
      if (resp.status === 429) {
        console.warn('[GreeksHeatmap] Live curves rate limited, retrying in 3s...');
        await new Promise(r => setTimeout(r, 3000));
        resp = await authFetch(url);
      }
      if (!resp.ok) throw new Error(`Live curves API error: ${resp.status}`);

      const data = await resp.json();
      for (const [dateStr, frag] of Object.entries(data.live_curves || {})) {
        const rows = frag?.rows || [];
        if (rows.length) this._liveFlipsHistory[dateStr] = rows;
      }
      this._liveCurvesSnapshot = data.current
        ? { date: data.current.date, curves: data.current.curves || null }
        : null;
      // Refit-health badge seed on load (the SSE row updates it live).
      // current.row.refit is null for pre-deploy rows; the badge is
      // null-tolerant.
      if (data.current) {
        this.options.onRefitHealth?.(data.current.row?.refit ?? null);
      }
      if (wantSnaps && data.snapshots) {
        for (const [dateStr, frag] of Object.entries(data.snapshots)) {
          const snaps = frag?.snapshots || {};
          this._liveCurvesHistory[dateStr] = Object.keys(snaps)
            .map(t => ({ time: parseInt(t, 10), curves: snaps[t] }))
            .sort((a, b) => a.time - b.time);
        }
      }
      return data;
    } catch (error) {
      console.error('[GreeksHeatmap] Live curves fetch error:', error);
      return null;
    }
  }

  /**
   * Fetch Exposure Profile rows from the dedicated endpoint at the chart's
   * display timeframe (renko/non-minute pages fall back to 1m). Rows are
   * LAST-derived from the 1m base server-side and arrive already
   * ES-converted with the per-greek fields (gex/dex/vex/cex + u_*); the
   * renderer normalizes per greek within the target row. Any failure
   * (including a 404 before the backend restart activates the route)
   * leaves the data null so existing flow is never broken.
   */
  /**
   * Merge overnight (ETH) per-date candles ahead of a base series' candles.
   * ETH minutes all precede 09:30, stored RTH rows are all at or after it,
   * and the serve-side forming guard keeps the injected forming column
   * strictly newer than the last stored eth bucket, so a plain concat stays
   * time-sorted with no duplicate times. Order is load-bearing:
   * _selectTargetRow breaks at the first at-or-before candle in a reverse
   * loop and the heatmaps take column width from the next array element.
   */
  _mergeEthRows(base, eth) {
    if (!eth || Object.keys(eth).length === 0) return base;
    const merged = { ...(base || {}) };
    for (const [dateKey, frag] of Object.entries(eth)) {
      const ethCandles = frag?.candles || [];
      if (!ethCandles.length) continue;
      const baseCandles = merged[dateKey]?.candles || [];
      merged[dateKey] = { ...(merged[dateKey] || {}), candles: ethCandles.concat(baseCandles) };
    }
    return merged;
  }

  async fetchExposureRows(startDate, endDate) {
    try {
      const expiryMode = this.options.expiryMode || '0dte';
      const underlying = this.options.underlying || 'spx';
      // rv is a cache-key version: bumping it evicts stale browser-cached
      // responses (rv=9: meta envelope + 9-field levels, the 3.5 reshape;
      // rv=8 was the overnight eth_rows/eth_t1_rows series).
      const url = `${this.options.apiUrl}/api/greeks/exposure-rows?start_date=${startDate}&end_date=${endDate}&expiry_mode=${expiryMode}&underlying=${underlying}&tf=${this._windowsTf()}${this.options.expProfileLiveSmile ? '&smile=live' : ''}&rv=9`;
      let resp = await authFetch(url);
      if (resp.status === 429) {
        console.warn('[GreeksHeatmap] Exposure rows rate limited, retrying in 3s...');
        await new Promise(r => setTimeout(r, 3000));
        resp = await authFetch(url);
      }
      if (!resp.ok) throw new Error(`Exposure rows API error: ${resp.status}`);

      const data = await resp.json();
      this.exposureRowsData = this._mergeEthRows(data.exposure_rows || null, data.eth_rows);
      this.t1RowsData = this._mergeEthRows(data.t1_rows || null, data.eth_t1_rows);
      // Column width follows the tf the backend actually served (it echoes
      // the request tf, falling back to 30m for an invalid one).
      this.options.exposureRowsTfSec = TF_SECONDS[data.meta?.tf] || 60;

      if (data.meta?.ratios_by_date) {
        this._ratiosByDate = { ...this._ratiosByDate, ...data.meta.ratios_by_date };
      }

      if (this.exposureRowsData) {
        console.log('[GreeksHeatmap] Exposure rows loaded:', Object.keys(this.exposureRowsData).length, 'sessions');
      }
      return data;
    } catch (error) {
      console.error('[GreeksHeatmap] Exposure rows fetch error:', error);
      this.exposureRowsData = null;
      return null;
    }
  }

  /**
   * Fetch the Hedging Demand series ({price, hedge} levels) from
   * /api/greeks/hedging-demand (3.5 split: HD no longer rides the
   * exposure-rows payload). Same params as the exposure fetch including
   * the smile source; hedge_rows/eth_rows merge exactly like the
   * exposure pair. Any failure leaves the container null so existing
   * flow is never broken.
   */
  async fetchHedgingDemand(startDate, endDate) {
    try {
      const expiryMode = this.options.expiryMode || '0dte';
      const underlying = this.options.underlying || 'spx';
      const url = `${this.options.apiUrl}/api/greeks/hedging-demand?start_date=${startDate}&end_date=${endDate}&expiry_mode=${expiryMode}&underlying=${underlying}&tf=${this._windowsTf()}${this.options.expProfileLiveSmile ? '&smile=live' : ''}&rv=1`;
      let resp = await authFetch(url);
      if (resp.status === 429) {
        console.warn('[GreeksHeatmap] Hedging demand rate limited, retrying in 3s...');
        await new Promise(r => setTimeout(r, 3000));
        resp = await authFetch(url);
      }
      if (!resp.ok) throw new Error(`Hedging demand API error: ${resp.status}`);

      const data = await resp.json();
      this.hedgeRowsData = this._mergeEthRows(data.hedge_rows || null, data.eth_rows);
      // Shared column-width option: both series serve the same tf.
      this.options.exposureRowsTfSec = TF_SECONDS[data.meta?.tf] || 60;

      if (data.meta?.ratios_by_date) {
        this._ratiosByDate = { ...this._ratiosByDate, ...data.meta.ratios_by_date };
      }

      if (this.hedgeRowsData) {
        console.log('[GreeksHeatmap] Hedging demand loaded:', Object.keys(this.hedgeRowsData).length, 'sessions');
      }
      return data;
    } catch (error) {
      console.error('[GreeksHeatmap] Hedging demand fetch error:', error);
      this.hedgeRowsData = null;
      return null;
    }
  }

  /**
   * Fetch IV-skew rows (per-minute per-strike call/put IVs of the
   * selected expiry mode's book) from /api/greeks/iv-skew. Rows arrive
   * ES-converted; they are bucketed here to the display tf keeping the
   * LAST 1m row per window (the exprow LAST-derived convention), so
   * _selectTargetRow scrubs them exactly like the exposure rows. Any
   * failure leaves the data null so existing flow is never broken.
   */
  async fetchIvSkew(startDate, endDate) {
    try {
      const underlying = this.options.underlying || 'spx';
      const expiryMode = this.options.expiryMode || '0dte';
      // rv=2: 5.9 meta envelope + row trim (points lose k, rows lose
      // spot_es/expiry, essvi projected to the 6 consumed keys)
      const url = `${this.options.apiUrl}/api/greeks/iv-skew?start_date=${startDate}&end_date=${endDate}&expiry_mode=${expiryMode}&underlying=${underlying}&rv=2`;
      let resp = await authFetch(url);
      if (resp.status === 429) {
        console.warn('[GreeksHeatmap] IV skew rate limited, retrying in 3s...');
        await new Promise(r => setTimeout(r, 3000));
        resp = await authFetch(url);
      }
      if (!resp.ok) throw new Error(`IV skew API error: ${resp.status}`);

      const data = await resp.json();
      const bucketSec = this._windowsTfSec();
      // The retarget guard in _selectTargetRow must use the SAME width
      // these rows are bucketed to (NOT the exposure tf, which is unset
      // when the Exposure Profile is off and then defaults to 60s,
      // snapping every historic hover back to the current row).
      this.options.ivSkewTfSec = bucketSec;
      const sessions = {};
      const ingest = (dateKey, row) => {
        if (!row || row.time == null || !Array.isArray(row.points) || !row.points.length) return;
        const bucketTime = Math.floor(row.time / bucketSec) * bucketSec;
        const candles = (sessions[dateKey] = sessions[dateKey] || { candles: [] }).candles;
        const last = candles[candles.length - 1];
        // essvi feeds the distribution band + the Smile Shape pane
        // series; rows lacking it just skip those consumers.
        const entry = {
          time: bucketTime, points: row.points,
          essvi: row.essvi || null,
        };
        if (last && last.time === bucketTime) candles[candles.length - 1] = entry;  // LAST row in window wins
        else candles.push(entry);
      };
      for (const [d, frag] of Object.entries(data.iv_skew || {})) {
        for (const row of (frag?.rows || [])) ingest(d, row);
      }
      // The open session's forming minute rides under 'current'
      if (data.current?.date) ingest(data.current.date, data.current.row);
      this.ivSkewData = Object.keys(sessions).length ? sessions : null;
      this._emitIvSkewShape();

      if (data.meta?.ratios_by_date) {
        this._ratiosByDate = { ...this._ratiosByDate, ...data.meta.ratios_by_date };
      }
      if (this.ivSkewData) {
        console.log('[GreeksHeatmap] IV skew loaded:', Object.keys(this.ivSkewData).length, 'sessions');
      }
      return data;
    } catch (error) {
      console.error('[GreeksHeatmap] IV skew fetch error:', error);
      this.ivSkewData = null;
      this._emitIvSkewShape();  // clears the IV Skew Shape pane on fetch failure
      return null;
    }
  }

  /**
   * Fetch volume rows (per-strike day-to-date CUMULATIVE traded volume of
   * the selected expiry mode's book) from /api/greeks/volume-rows. Rows
   * arrive ES-converted and LAST-derived per display window (the exprow
   * convention), so _selectTargetRow scrubs them exactly like the exposure
   * rows; the draw derives a window's traded volume by subtracting the
   * previous window's row. Any failure (including a backend without the
   * endpoint yet) leaves the data null so existing flow is never broken.
   */
  async fetchVolumeRows(startDate, endDate) {
    try {
      const expiryMode = this.options.expiryMode || '0dte';
      const underlying = this.options.underlying || 'spx';
      // rv=3: 5.9 meta envelope (rv=2 was the VOLROW v2 aggressor sub-fields)
      const url = `${this.options.apiUrl}/api/greeks/volume-rows?start_date=${startDate}&end_date=${endDate}&expiry_mode=${expiryMode}&underlying=${underlying}&tf=${this._windowsTf()}&rv=3`;
      let resp = await authFetch(url);
      if (resp.status === 429) {
        console.warn('[GreeksHeatmap] Volume rows rate limited, retrying in 3s...');
        await new Promise(r => setTimeout(r, 3000));
        resp = await authFetch(url);
      }
      if (!resp.ok) throw new Error(`Volume rows API error: ${resp.status}`);

      const data = await resp.json();
      const sessions = {};
      for (const [d, candles] of Object.entries(data.volume_rows || {})) {
        if (Array.isArray(candles) && candles.length) sessions[d] = { candles };
      }
      this.volumeRowsData = Object.keys(sessions).length ? sessions : null;
      // The retarget guard in _selectTargetRow must use the SAME width
      // these rows were bucketed to (NOT the exposure tf, which is unset
      // when the Exposure Profile is off; the ivSkewTfSec precedent).
      this.options.optVolTfSec = TF_SECONDS[data.meta?.tf] || this._windowsTfSec();

      if (data.meta?.ratios_by_date) {
        this._ratiosByDate = { ...this._ratiosByDate, ...data.meta.ratios_by_date };
      }
      if (this.volumeRowsData) {
        console.log('[GreeksHeatmap] Volume rows loaded:', Object.keys(this.volumeRowsData).length, 'sessions');
      }
      this._emitVolFlow();
      return data;
    } catch (error) {
      console.error('[GreeksHeatmap] Volume rows fetch error:', error);
      this.volumeRowsData = null;
      this._emitVolFlow();  // clears the Flow pane on fetch failure
      return null;
    }
  }

  /**
   * Connect to SSE for real-time updates via unified SSE client
   */
  connectSSE() {
    this.disconnectSSE();

    const channel = `greeks:${this.options.underlying || 'spx'}:${this.options.timeframe}`;
    console.log('[GreeksHeatmap] Subscribing to unified SSE channel:', channel);

    this._sseUnsubscribers = [];

    // Exposure Profile / Hedging Demand live rows. The backend only ever forwards the forming
    // exposure row on the greeks 1m realtime channel (verified in api/server.py:
    // greeks:updates:realtime:{ul}:1m -> SSE greeks:{ul}:1m), regardless of the
    // page timeframe, so subscribe on the dedicated 1m channel here. Going through
    // unifiedSSE.subscribe lets the register-only branch attach the listener when
    // the channel is already on the wire, else it reconnects to add it. Skip renko
    // (non-minute) pages: their timeframe is not a real candle tf.
    const tfStr = this.options.timeframe || '';
    if (!tfStr.includes('renko')) {
      const underlyingKey = this.options.underlying || 'spx';
      const exposureChannel = `greeks:${underlyingKey}:1m`;

      // Today's forming row and the T+1 forming row (next session's book,
      // separate SSE event so old clients drop it instead of mis-ingesting
      // it into the today series) share the ingest path; only the gate and
      // the target container differ.
      const handleExposureRow = (message) => {
        try {
          // Filter by expiry mode + underlying like the other greeks handlers
          if (message.mode && message.mode !== (this.options.expiryMode || '0dte')) return;
          if (message.underlying && message.underlying !== underlyingKey) return;
          if (!this.options.showExposureProfile && !this.options.showHedgingDemand) return;
          // Source gate: when the Live smile source is on, the today
          // container holds live-smile rows and only the live event feeds it
          if (this.options.expProfileLiveSmile) return;
          this._upsertLiveExposureRow(message, 'exposureRowsData');
        } catch (e) {
          console.error('[GreeksHeatmap] greeks_exposure_row error:', e);
        }
      };

      // Live-smile variant of the forming-row upsert: same container, only
      // active when the Live smile source is selected.
      const handleExposureRowLive = (message) => {
        try {
          if (message.mode && message.mode !== (this.options.expiryMode || '0dte')) return;
          if (message.underlying && message.underlying !== underlyingKey) return;
          if (!this.options.showExposureProfile && !this.options.showHedgingDemand) return;
          if (!this.options.expProfileLiveSmile) return;
          this._upsertLiveExposureRow(message, 'exposureRowsData');
        } catch (e) {
          console.error('[GreeksHeatmap] greeks_exposure_row_live error:', e);
        }
      };

      const handleExposureRowT1 = (message) => {
        try {
          if (message.mode && message.mode !== (this.options.expiryMode || '0dte')) return;
          if (message.underlying && message.underlying !== underlyingKey) return;
          if (!this.options.showExposureProfile || !this.options.showExpProfileT1) return;
          this._upsertLiveExposureRow(message, 't1RowsData');
        } catch (e) {
          console.error('[GreeksHeatmap] greeks_exposure_row_t1 error:', e);
        }
      };

      // Live Flips + live Projected Curves: per-minute live-curves row
      // (SPX-space payload with the session ratio; convert here, mirroring
      // the REST fragment path). The full-curve refresh re-fetches the
      // current snapshot (the SSE row carries crossings only) and appends
      // it to the local scrub history so the whole day is never re-fetched.
      const handleLiveCurves = (message) => {
        try {
          if (message.mode && message.mode !== (this.options.expiryMode || '0dte')) return;
          if (message.underlying && message.underlying !== underlyingKey) return;
          const row = message.data;
          if (!row) return;
          // Refit-health badge rides EVERY row for the selected mode,
          // before the sub-layer gate below: the OI Data selector alone
          // must keep the state line fresh even with the flip/curve
          // sub-layers toggled off.
          this.options.onRefitHealth?.(row.refit ?? null);
          const flipsOn = this.options.showZeroLevels && this.options.showZeroLevelsLive;
          const curvesOn = this.options.showProjectedCurves && this.options.projCurvesLive;
          if (!flipsOn && !curvesOn) return;
          if (flipsOn) {
            const ratio = this._resolveRowRatio(row, message,
              getOpenSessionDate() || new Date().toISOString().split('T')[0]);
            if (ratio) {
              const toEs = (v) => (v == null ? null : Math.round((v * ratio) / 0.25) * 0.25);
              const flip = {};
              const crossings = {};
              for (const g of ['gex', 'dex', 'vex', 'cex']) {
                flip[g] = toEs((row.flip || {})[g]);
                crossings[g] = ((row.crossings || {})[g] || []).map(toEs);
              }
              const todayStr = new Date().toISOString().split('T')[0];
              const hist = this._liveFlipsHistory[todayStr] = this._liveFlipsHistory[todayStr] || [];
              if (!hist.length || hist[hist.length - 1].time < row.time) {
                hist.push({ time: row.time, spot_es: row.spot_es,
                            refit_ok: row.refit_ok, refit: row.refit,
                            flip, crossings });
              }
              this.drawSessionLevels();
            }
          }
          if (curvesOn) {
            this.fetchLiveCurves(null, true).then(() => {
              const snap = this._liveCurvesSnapshot;
              if (snap?.date && snap.curves && row.time != null) {
                const hist = this._liveCurvesHistory[snap.date] = this._liveCurvesHistory[snap.date] || [];
                if (!hist.length || hist[hist.length - 1].time < row.time) {
                  hist.push({ time: row.time, curves: snap.curves });
                }
              }
              this.draw();
            }).catch(() => {});
          }
        } catch (e) {
          console.error('[GreeksHeatmap] greeks_live_curves error:', e);
        }
      };

      // IV Skew forming row (each underlying's streamer publishes one
      // per expiry mode on its channel; keep only the selected mode's)
      const handleIvSkew = (message) => {
        try {
          if (message.mode && message.mode !== (this.options.expiryMode || '0dte')) return;
          if (message.underlying && message.underlying !== underlyingKey) return;
          if (!this.options.showIvSkew) return;
          this._upsertLiveIvSkewRow(message);
        } catch (e) {
          console.error('[GreeksHeatmap] greeks_iv_skew error:', e);
        }
      };

      // Options Volume forming row (day-to-date cumulative per-strike
      // volume). REPLACE upsert is correct because rows are cumulative:
      // the latest row IS the window's LAST-derived value.
      const handleVolumeRow = (message) => {
        try {
          if (message.mode && message.mode !== (this.options.expiryMode || '0dte')) return;
          if (message.underlying && message.underlying !== underlyingKey) return;
          if (!this.options.showOptionsVolume) return;
          this._upsertLiveVolumeRow(message);
        } catch (e) {
          console.error('[GreeksHeatmap] greeks_volume_row error:', e);
        }
      };

      // ATM IV tick for the realized Hedging Demand mode. The plugin keeps
      // its OWN flattened copy (_quoteIvFlat), so this never conflicts with
      // QuoteIVIndicator's separate subscription/state when the IV Index is
      // also on. Gated on the mode flag; redraws throttled to 5s.
      const handleQuoteIvTick = (data) => {
        try {
          if (!this.options.hedgeDemandRealized || !this.options.showHedgingDemand
              || !this._realizedHedgeSupported()) return;
          if (!data || data.time == null || typeof data.atm_iv !== 'number') return;
          // The shared 'quote-iv' SSE channel carries every underlying's
          // ticks; keep only the selected one. Untagged ticks (pre-restart
          // backend) are accepted on SPX only, matching the old effective
          // behavior without blanking SPX in the deploy window.
          const selUl = underlyingKey || 'spx';
          if (data.underlying === undefined) {
            if (selUl !== 'spx') return;
          } else if (data.underlying !== selUl) {
            return;
          }
          const flat = this._quoteIvFlat = this._quoteIvFlat || [];
          if (!flat.length || flat[flat.length - 1].time < data.time) {
            flat.push({ time: data.time, value: data.atm_iv });
          } else if (flat[flat.length - 1].time === data.time) {
            flat[flat.length - 1] = { time: data.time, value: data.atm_iv };
          }
          const now = Date.now();
          if (this.enabled && now - this._lastIvDrawTs > 5000) {
            this._lastIvDrawTs = now;
            this.draw();
          }
        } catch (e) {
          console.error('[GreeksHeatmap] quote_iv_update error:', e);
        }
      };

      // Server-inserted overflow marker: a greeks_live_curves row was
      // evicted from this client's SSE queue (stalled tab), leaving a
      // permanent hole in the append-only scrub histories. Heal by
      // re-running the authoritative REST live-curves fetch (wholesale
      // per-day replace); throttled so a marker burst fires one refetch.
      const handleGap = () => {
        try {
          // Only the live flip/curve sub-layers consume the histories the
          // refetch heals; skip entirely when both are off.
          const flipsOn = this.options.showZeroLevels && this.options.showZeroLevelsLive;
          const curvesOn = this.options.showProjectedCurves && this.options.projCurvesLive;
          if (!flipsOn && !curvesOn) return;
          const now = Date.now();
          if (now - (this._lastGapRefetch || 0) < 5000) return;
          this._lastGapRefetch = now;
          console.warn('[GreeksHeatmap] SSE gap marker received, refetching live curves');
          this.fetchLiveCurves(null).then(() => {
            this.drawSessionLevels();
            this.draw();
          }).catch(() => {});
        } catch (e) { /* gap heal must never break the plugin */ }
      };

      this._sseUnsubscribers.push(
        unifiedSSE.subscribe(exposureChannel, 'greeks_exposure_row', handleExposureRow),
        unifiedSSE.subscribe(exposureChannel, 'greeks_exposure_row_t1', handleExposureRowT1),
        unifiedSSE.subscribe(exposureChannel, 'greeks_exposure_row_live', handleExposureRowLive),
        unifiedSSE.subscribe(exposureChannel, 'greeks_live_curves', handleLiveCurves),
        unifiedSSE.subscribe(exposureChannel, 'greeks_iv_skew', handleIvSkew),
        unifiedSSE.subscribe(exposureChannel, 'greeks_volume_row', handleVolumeRow),
        unifiedSSE.subscribe(exposureChannel, 'gap', handleGap),
        unifiedSSE.subscribe('quote-iv', 'quote_iv_update', handleQuoteIvTick)
      );
    }
  }

  /**
   * Ingest a live forming exposure row (today or T+1 series) into the
   * per-session candle container named by containerField, converting SPX
   * strikes to ES price space, then repaint.
   */
  _upsertLiveExposureRow(message, containerField) {
    const row = message.data;
    if (!row || !Array.isArray(row.levels) || row.time == null) return;

    this._applyMessageMetadata(message);

    // Resolve the session date: the forming row is by definition the
    // currently OPEN session (the UTC-date fallback mislabels rows
    // arriving 18:00-20:00 ET with the prior calendar date). T+1 rows
    // always carry their session date in row.date.
    const dateStr = row.date || getOpenSessionDate()
      || new Date(row.time * 1000).toISOString().split('T')[0];
    // ES/SPX ratio via the canonical chain (session anchor leads, matching
    // the server-side conversion of the day's settled rows).
    const ratio = this._resolveRowRatio(row, message, dateStr);
    if (!ratio) return;  // Cannot place the row in ES space yet

    // Carry the per-greek fields through verbatim; the renderer
    // normalizes per greek within the target row. The SSE payload is the
    // stored-row vocabulary (spx_strike + components + total_hedge_es):
    // the hedge value feeds the SEPARATE hedge container (3.5 split), it
    // no longer rides on the exposure levels.
    const esLevels = [];
    const hedgeLevels = [];
    for (const lv of row.levels) {
      if (lv.spx_strike == null) continue;
      const price = roundToTick(lv.spx_strike * ratio);
      esLevels.push({
        price,
        gex: lv.gex || 0, dex: lv.dex || 0, vex: lv.vex || 0, cex: lv.cex || 0,
        u_gex: lv.u_gex || 0, u_dex: lv.u_dex || 0, u_vex: lv.u_vex || 0, u_cex: lv.u_cex || 0,
      });
      if (lv.total_hedge_es != null) {
        hedgeLevels.push({ price, hedge: lv.total_hedge_es });
      }
    }
    if (esLevels.length === 0) return;

    // Map the forming 1m row to its parent display-tf bucket and
    // upsert that column so the last column updates in place.
    const bucketSec = this._windowsTfSec();
    const bucketTime = Math.floor(row.time / bucketSec) * bucketSec;
    const upsert = (field, levels) => {
      if (!this[field]) this[field] = {};
      if (!this[field][dateStr]) this[field][dateStr] = { candles: [] };
      const candles = this[field][dateStr].candles;
      const idx = candles.findIndex(c => c.time === bucketTime);
      if (idx >= 0) {
        candles[idx] = { time: bucketTime, levels };
      } else {
        candles.push({ time: bucketTime, levels });
        candles.sort((a, b) => a.time - b.time);
      }
    };
    upsert(containerField, esLevels);
    // The today-series forming row also updates the Hedging Demand live
    // column (HD never renders T+1, so the t1 ingest skips this).
    if (containerField === 'exposureRowsData' && hedgeLevels.length > 0) {
      upsert('hedgeRowsData', hedgeLevels);
    }

    // Repaint. draw() rebinds the (possibly newly created) container
    // reference into a fresh primitive; forming rows arrive at most once
    // every 5s (producer-throttled), so the full draw cost is negligible.
    if (this.enabled) {
      this.draw();
    }
  }

  /**
   * Ingest the live forming Options Volume row (SPX-space SSE payload;
   * volumes are day-to-date cumulative): convert strikes to ES (REST
   * ratios first, then the message ratio hint; volume rows carry no
   * spots), bucket to the display tf and REPLACE that column: the latest
   * cumulative row IS the window's correct LAST-derived value, so replace
   * semantics stay exact and self-correct after any missed update.
   */
  _upsertLiveVolumeRow(message) {
    const row = message.data;
    if (!row || !Array.isArray(row.levels) || row.time == null) return;

    this._applyMessageMetadata(message);

    const dateStr = row.date || getOpenSessionDate()
      || new Date(row.time * 1000).toISOString().split('T')[0];
    // Canonical chain; volume rows carry no in-row basis, so this resolves
    // REST-ratio-then-live-hint (the hint places overnight forming rows).
    const ratio = this._resolveRowRatio(row, message, dateStr);
    if (!ratio) return;  // Cannot place the row in ES space yet

    const esLevels = [];
    for (const lv of row.levels) {
      if (lv.spx_strike == null) continue;
      esLevels.push({
        price: roundToTick(lv.spx_strike * ratio),
        call_vol: lv.call_vol || 0,
        put_vol: lv.put_vol || 0,
        call_vol_ask: lv.call_vol_ask || 0,
        call_vol_bid: lv.call_vol_bid || 0,
        put_vol_ask: lv.put_vol_ask || 0,
        put_vol_bid: lv.put_vol_bid || 0,
      });
    }
    if (esLevels.length === 0) return;

    const bucketSec = this._windowsTfSec();
    const bucketTime = Math.floor(row.time / bucketSec) * bucketSec;
    if (!this.volumeRowsData) this.volumeRowsData = {};
    if (!this.volumeRowsData[dateStr]) this.volumeRowsData[dateStr] = { candles: [] };
    const candles = this.volumeRowsData[dateStr].candles;
    const idx = candles.findIndex(c => c.time === bucketTime);
    if (idx >= 0) {
      candles[idx] = { time: bucketTime, levels: esLevels };
    } else {
      candles.push({ time: bucketTime, levels: esLevels });
      candles.sort((a, b) => a.time - b.time);
    }
    this._emitVolFlow();
    if (this.enabled) {
      this.draw();
    }
  }

  /**
   * Ingest the live forming IV-smile row (SPX-space SSE payload):
   * convert strikes to ES (REST ratios first, then the row's own spots,
   * mirroring _upsertLiveExposureRow's resolution order), bucket to the
   * display tf (LAST 1m row in the window wins), repaint.
   */
  _upsertLiveIvSkewRow(message) {
    const row = message.data;
    if (!row || !Array.isArray(row.points) || row.time == null) return;

    this._applyMessageMetadata(message);

    const dateStr = getOpenSessionDate()
      || new Date(row.time * 1000).toISOString().split('T')[0];
    // Canonical chain; the session anchor leads (static placement), with
    // the row's exact ratio field as the anchor-missing fallback.
    const ratio = this._resolveRowRatio(row, message, dateStr);
    if (!ratio) return;  // Cannot place the row in ES space yet

    const points = [];
    for (const pt of row.points) {
      if (pt.k == null) continue;
      points.push({
        price: roundToTick(pt.k * ratio), k: pt.k,
        c: pt.c ?? null, p: pt.p ?? null,
        fc: pt.fc || 0, fp: pt.fp || 0,
      });
    }
    if (!points.length) return;

    const bucketSec = this._windowsTfSec();
    this.options.ivSkewTfSec = bucketSec;
    const bucketTime = Math.floor(row.time / bucketSec) * bucketSec;
    if (!this.ivSkewData) this.ivSkewData = {};
    if (!this.ivSkewData[dateStr]) this.ivSkewData[dateStr] = { candles: [] };
    const candles = this.ivSkewData[dateStr].candles;
    const idx = candles.findIndex(c => c.time === bucketTime);
    // entry.ratio records the ratio the points were placed with (the
    // session anchor when available); the derived overlays reuse it so
    // the band always matches the dots.
    const entry = {
      time: bucketTime, points,
      essvi: row.essvi || null, ratio,
    };
    if (idx >= 0) {
      candles[idx] = entry;
    } else {
      candles.push(entry);
      candles.sort((a, b) => a.time - b.time);
    }
    this._emitIvSkewShape();

    if (this.enabled) {
      this.draw();
    }
  }

  /**
   * Emit the per-minute smile-shape series (SK/CU/FIT) to the Smile
   * Shape pane via options.onIvSkewShape. Values are ratio-independent
   * slice geometry (atmSkew/atmCurvature/rmse), memoized per candle;
   * FIT converts to vol points. No-op without a consumer or data.
   */
  _emitIvSkewShape() {
    const cb = this.options.onIvSkewShape;
    if (typeof cb !== 'function' || !this.options.ivSkewShapePane) return;
    if (!this.ivSkewData) {
      // Explicit empty emission so the pane CLEARS on a no-data context
      // (underlying/date switch with no smile rows) instead of holding
      // the previous context's series.
      try { cb({}); } catch (e) { /* consumer errors must not break the plugin */ }
      return;
    }
    const out = {};
    for (const dateKey of Object.keys(this.ivSkewData)) {
      const rows = [];
      for (const candle of this.ivSkewData[dateKey].candles || []) {
        let v = SMILE_SHAPE_CACHE.get(candle);
        if (!v) {
          const es = candle.essvi;
          v = (es && es.theta > 0 && es.forward > 0 && es.tau > 1e-10)
            ? { sk: atmSkew(es), cu: atmCurvature(es), fit: (es.rmse ?? NaN) * 100 }
            : {};
          SMILE_SHAPE_CACHE.set(candle, v);
        }
        if (v.sk !== undefined) {
          rows.push({ time: candle.time, sk: v.sk, cu: v.cu, fit: v.fit });
        }
      }
      if (rows.length) out[dateKey] = rows;
    }
    try {
      cb(out);
    } catch (e) {
      // Pane consumer errors must never break the plugin
    }
  }

  /**
   * Disconnect from SSE by unsubscribing all handlers
   */
  disconnectSSE() {
    if (this._sseUnsubscribers) {
      this._sseUnsubscribers.forEach(unsub => unsub());
      this._sseUnsubscribers = null;
    }
  }

  _applyMessageMetadata(message) {
    if (message.es_spx_ratio) {
      // Seed the open session's ratio ONLY when absent (overnight, before
      // any REST metadata exists): the live instantaneous hint must never
      // overwrite the REST T-1 session anchor, or level placement would
      // silently shift. Keyed by open session date so the seed lands under
      // the key the _resolveRowRatio anchor lookups use (UTC-today
      // mislabels rows arriving 18:00-20:00 ET).
      const sessionStr = getOpenSessionDate() || new Date().toISOString().split('T')[0];
      if (this._ratiosByDate[sessionStr] == null) {
        this._ratiosByDate[sessionStr] = message.es_spx_ratio;
      }
    }
    // NOTE: heatmap cell heights are sized per column from the row's own
    // strike spacing (_minLevelGap).
  }

  /**
   * Single source of truth for a row's ES/SPX conversion ratio (canonical
   * precedence): the REST per-session T-1 anchor first, then the row's own
   * exact ratio field, then the row's own spots (the producing streamer's
   * conversion basis), then the SSE envelope's live-ratio hint. Anchor-first
   * keeps every level STATIC for the whole session (no re-basing when the
   * live ratio appears at the 09:30 cash open) and matches both the
   * server-side conversion of settled rows and the REST forming-row
   * injection (greeks.py _inject_forming). The row-basis fallbacks still
   * place T+1 rows (whose session anchor may not exist yet), and the hint
   * is what places overnight forming volume rows before any REST metadata
   * exists. Returns a positive ratio or null.
   */
  _resolveRowRatio(row, message, dateStr) {
    const anchor = Number(this._ratiosByDate[dateStr]);
    if (anchor > 0) return anchor;
    if (row && row.ratio > 0) return row.ratio;
    if (row && row.spot_es > 0 && row.spot_spx > 0) return row.spot_es / row.spot_spx;
    const r = (message && message.es_spx_ratio) || null;
    return r > 0 ? r : null;
  }

  /**
   * Draw the OI overlays (OI distribution, projected curves, Exposure Profile,
   * Hedging Demand) into a fresh primitive.
   */
  draw() {
    if (!this.enabled || (!this.oiBarsData && !this.oiCurvesData && !this.exposureRowsData && !this.hedgeRowsData && !this.ivSkewData && !this.volumeRowsData)) {
      console.warn('[GreeksHeatmap] Cannot draw - enabled:', this.enabled, 'oi:', !!this.oiBarsData, 'curves:', !!this.oiCurvesData, 'exposure:', !!this.exposureRowsData, 'hedge:', !!this.hedgeRowsData, 'smile:', !!this.ivSkewData, 'volume:', !!this.volumeRowsData);
      return;
    }

    if (!this.series || !this.chart) {
      console.warn('[GreeksHeatmap] Cannot draw - series:', !!this.series, 'chart:', !!this.chart);
      return;
    }

    // Determine if viewing live day (show direction arrows) or historic (hide them)
    const isLiveDay = this.lastFetchParams?.isLiveDay ?? true;

    // Remove existing primitive
    this.clear();

    // Merge isLiveDay, sessionLevelData, and OI overlay data into options for this render
    const drawOptions = {
      ...this.options,
      isLiveDay,
      sessionLevelData: this.sessionLevelData,
      oiBarsData: this.oiBarsData,
      isOptionsHoliday: this._isOptionsHoliday || false,
      oiCurvesData: this._effectiveOiCurvesData(),
      liveCurvesHistory: this._liveCurvesHistory,
      exposureRowsData: this.exposureRowsData,
      hedgeRowsData: this.hedgeRowsData,
      t1RowsData: this.t1RowsData,
      ivSkewData: this.ivSkewData,
      volumeRowsData: this.volumeRowsData,
      ratiosByDate: this._ratiosByDate,
      quoteIvFlat: this._quoteIvFlat,
      crosshairRef: this._crosshairRef,
      anchorRef: this._anchorRef,
      anchorHitRef: this._anchorHitRef
    };

    // Create new primitive from the merged draw options
    this.primitive = new GreeksHeatmapPrimitive(
      this.series,
      this.chart,
      drawOptions
    );

    try {
      this.series.attachPrimitive(this.primitive);
      // Immediate requestUpdate to trigger the initial paint
      this.primitive.requestUpdate();
      // Deferred requestUpdate as safety net - some chart internals batch/suppress
      // updates during attachPrimitive processing, so the synchronous call above
      // may be swallowed. Guard against stale primitive in case draw() is called
      // again before the rAF fires.
      const prim = this.primitive;
      requestAnimationFrame(() => {
        if (prim === this.primitive && prim._requestUpdate) prim.requestUpdate();
      });
    } catch (err) {
      console.error('[GreeksHeatmap] Error attaching primitive:', err);
    }

    // Subscribe to crosshair moves for curve dot markers
    this._ensureCrosshairSubscription();
  }

  /**
   * Register the shared crosshair handler once. Called from draw() AND
   * drawSessionLevels() so each hover-scrubbing indicator (Projected
   * Curves, Exposure Profile, IV Skew, Zero Levels) registers it
   * independently: Zero Levels alone never goes through draw().
   */
  _ensureCrosshairSubscription() {
    if (this._crosshairHandler) return;
    this._crosshairHandler = (param) => {
      const prevPrice = this._crosshairRef.price;
      const prevTime = this._crosshairRef.time;
      if (!param.point || param.point.y < 0) {
        this._crosshairRef.price = null;
        this._crosshairRef.time = null;
      } else {
        this._crosshairRef.price = this.series.coordinateToPrice(param.point.y);
        this._crosshairRef.time = param.time || null;
      }
      // Repaint if price changed (projected-curve dot) or time changed
      // (Exposure Profile / Hedging Demand / IV Skew column scrubbing).
      // Settled Zero Levels are session-static; only the live sub-layer
      // repaints on hover.
      const priceChanged = this._crosshairRef.price !== prevPrice;
      const timeChanged = this._crosshairRef.time !== prevTime;
      if ((priceChanged || timeChanged) && this.options.showProjectedCurves && this.oiCurvesData) {
        if (this.primitive) this.primitive.requestUpdate();
      }
      if (timeChanged && ((this.options.showExposureProfile && this.exposureRowsData)
          || (this.options.showHedgingDemand && this.hedgeRowsData))) {
        if (this.primitive) this.primitive.requestUpdate();
      }
      if (timeChanged && this.options.showIvSkew && this.ivSkewData) {
        if (this.primitive) this.primitive.requestUpdate();
      }
      if (timeChanged && this.options.showOptionsVolume && this.volumeRowsData) {
        if (this.primitive) this.primitive.requestUpdate();
      }
      if (timeChanged && this.options.showZeroLevels && this.options.showZeroLevelsLive
          && this.sessionLevelPrimitive) {
        this.sessionLevelPrimitive.requestUpdate();
      }
    };
    this.chart.subscribeCrosshairMove(this._crosshairHandler);
  }

  clear() {
    if (this.primitive) {
      try {
        this.series.detachPrimitive(this.primitive);
      } catch (e) {
        console.warn('[GreeksHeatmap] Error detaching primitive:', e);
      }
      this.primitive = null;
    }
  }

  /**
   * Enable with injected data instead of fetching. No API, no SSE. Used by
   * the docs MiniChart to render fixture snapshots; the live chart keeps
   * using enable().
   *
   * @param {Object|null} oiBars - /api/greeks/oi-bars response shape
   *   ({meta: {ratios_by_date, is_options_holiday}, oi_bars: {date: {bars, globex_open_ts, rth_open_ts}}})
   * @param {Object|null} exposure - /api/greeks/exposure-rows response shape
   *   ({meta: {tf, ratios_by_date}, exposure_rows: {date: {candles}}, t1_rows})
   */
  setStaticData({ oiBars = null, exposure = null } = {}) {
    const hasOiBars = oiBars?.oi_bars && Object.keys(oiBars.oi_bars).length > 0;
    const hasExposure = exposure?.exposure_rows && Object.keys(exposure.exposure_rows).length > 0;
    if (!hasOiBars && !hasExposure) return;
    this._enableGeneration++;

    if (hasOiBars) {
      this.oiBarsData = oiBars.oi_bars;
      this._isOptionsHoliday = oiBars.meta?.is_options_holiday || false;
      if (oiBars.meta?.ratios_by_date) {
        this._ratiosByDate = { ...this._ratiosByDate, ...oiBars.meta.ratios_by_date };
      }
    }

    if (hasExposure) {
      this.exposureRowsData = this._mergeEthRows(exposure.exposure_rows, exposure.eth_rows);
      this.t1RowsData = this._mergeEthRows(exposure.t1_rows || null, exposure.eth_t1_rows);
      this.options.exposureRowsTfSec = TF_SECONDS[exposure.meta?.tf] || 1800;
      if (exposure.meta?.ratios_by_date) {
        this._ratiosByDate = { ...this._ratiosByDate, ...exposure.meta.ratios_by_date };
      }
    }

    this.lastFetchParams = { isLiveDay: false };
    this.enabled = true;
    this.draw();
  }

  async enable(date) {
    // Increment generation so any in-flight enable from a previous call is invalidated
    const generation = ++this._enableGeneration;

    // Don't set enabled until data is fetched to prevent race conditions
    // with candle data updates that trigger draw() before data is ready
    await this.fetchData(date);

    // If disable() or a newer enable() was called during fetch, bail out
    if (generation !== this._enableGeneration) {
      console.log('[GreeksHeatmap] Enable cancelled (stale generation)');
      return;
    }

    // Set enabled only after data is fetched
    this.enabled = true;

    const hasOiBars = this.oiBarsData && Object.values(this.oiBarsData).some(s => s.bars?.length > 0);
    const hasCurves = !!this.oiCurvesData;
    const hasExposure = !!this.exposureRowsData;
    const hasHedge = !!this.hedgeRowsData;
    const hasIvSkew = !!this.ivSkewData;
    if (hasOiBars || hasCurves || hasExposure || hasHedge || hasIvSkew) {
      this.draw();
    } else {
      console.log('[GreeksHeatmap] No data available');
    }

    this.connectSSE();

    // Subscribe to series data changes so that when new ES candles arrive,
    // we can redraw the OI overlays (their columns/curves are placed against the
    // ES time axis, which may not have been laid out when the overlay data loaded)
    this._dataChangedHandler = () => {
      if (this.enabled) {
        // Reset debounce on each event so the final draw always runs
        // after the last ES candle arrives (not just the first)
        if (this._dataChangeRedrawTimeout) {
          clearTimeout(this._dataChangeRedrawTimeout);
        }
        this._dataChangeRedrawTimeout = setTimeout(() => {
          this._dataChangeRedrawTimeout = null;
          this.draw();
        }, 300);
      }
    };
    this.series.subscribeDataChanged(this._dataChangedHandler);
  }

  disable() {
    this._enableGeneration++; // Invalidate any in-flight enable()
    this.enabled = false;
    this.clear();

    // Release data to free memory: enable() replaces all via fetchData()
    this.oiBarsData = null;
    this.oiCurvesData = null;
    this.exposureRowsData = null;
    this.hedgeRowsData = null;
    this.t1RowsData = null;
    this.ivSkewData = null;
    this.volumeRowsData = null;

    // Also clear session level primitive
    if (this.sessionLevelPrimitive) {
      try {
        this.series.detachPrimitive(this.sessionLevelPrimitive);
      } catch (e) {
        // Ignore detach errors
      }
      this.sessionLevelPrimitive = null;
    }

    this.disconnectSSE();

    // Clear any pending draw retry
    if (this._drawRetryTimeout) {
      clearTimeout(this._drawRetryTimeout);
      this._drawRetryTimeout = null;
    }

    // Unsubscribe from series data changes
    if (this._dataChangedHandler) {
      this.series.unsubscribeDataChanged(this._dataChangedHandler);
      this._dataChangedHandler = null;
    }

    // Clear any pending data change redraw
    if (this._dataChangeRedrawTimeout) {
      clearTimeout(this._dataChangeRedrawTimeout);
      this._dataChangeRedrawTimeout = null;
    }

    // Clear any pending throttled draw
    if (this._throttledDrawTimer) {
      clearTimeout(this._throttledDrawTimer);
      this._throttledDrawTimer = null;
    }

    // Unsubscribe from crosshair moves
    if (this._crosshairHandler) {
      this.chart.unsubscribeCrosshairMove(this._crosshairHandler);
      this._crosshairHandler = null;
      this._crosshairRef.price = null;
    }
  }

  async toggle(date) {
    if (this.enabled) {
      this.disable();
    } else {
      await this.enable(date);
    }
    return this.enabled;
  }

  async update(date, timeframe = null) {
    if (!this.enabled) return;

    if (timeframe) {
      this.options.timeframe = timeframe;
      // Reconnect SSE at new timeframe URL
      this.connectSSE();
    }

    const data = await this.fetchData(date, timeframe);

    if (data) {
      this.draw();
    }
  }

  isEnabled() {
    return this.enabled;
  }

  getOIBarsData() {
    return this.oiBarsData;
  }

  /**
   * Look up projected curve GEX/DEX/VEX values at a given ES price.
   * Searches all visible sessions (developing first, then completed).
   * Returns { gex, dex, vex, sessionDate } or null.
   */
  getCurveValuesAtPrice(esPrice, timestamp = null) {
    if (!this.oiCurvesData || esPrice == null) return null;

    const GLOBEX_DURATION = 82800;

    const sessions = Object.values(this.oiCurvesData)
      .filter(s => s.globex_open_ts)
      .sort((a, b) => a.globex_open_ts - b.globex_open_ts);

    // If timestamp provided, find the session that contains it
    let target = null;
    if (timestamp != null) {
      for (const s of sessions) {
        const globexClose = s.globex_open_ts + GLOBEX_DURATION;
        if (s.globex_open_ts <= timestamp && timestamp < globexClose) { target = s; break; }
      }
      // Timestamp was provided but no session matched - don't fall back to a wrong session
      if (!target) return null;
    }

    // No timestamp: fall back to developing session, then most recent completed
    if (!target) {
      const now = Math.floor(Date.now() / 1000);
      for (const s of sessions) {
        const globexClose = s.globex_open_ts + GLOBEX_DURATION;
        if (s.globex_open_ts <= now && now < globexClose) { target = s; break; }
      }
      if (!target) {
        for (let i = sessions.length - 1; i >= 0; i--) {
          const globexClose = sessions[i].globex_open_ts + GLOBEX_DURATION;
          if (globexClose <= now) { target = sessions[i]; break; }
        }
      }
    }
    if (!target) return null;

    // Curves are static per session (canonical open-tau bake), so the
    // hovered timestamp only selects the session, never a window within it.
    const curveSource = target;

    // Curve keys are already in ES price space (converted by API via spx_to_es_price)
    const lookupPrice = esPrice;

    const result = {};
    for (const key of ['gex', 'dex', 'vex', 'cex']) {
      const curve = curveSource[`${key}_curve`];
      if (!curve || typeof curve !== 'object') { result[key] = null; continue; }

      const entries = Object.entries(curve)
        .map(([k, v]) => [parseFloat(k), v])
        .sort((a, b) => a[0] - b[0]);
      if (entries.length === 0) { result[key] = null; continue; }

      // Clamp or interpolate
      if (lookupPrice <= entries[0][0]) { result[key] = entries[0][1]; continue; }
      if (lookupPrice >= entries[entries.length - 1][0]) { result[key] = entries[entries.length - 1][1]; continue; }

      // Linear interpolation
      for (let i = 0; i < entries.length - 1; i++) {
        if (lookupPrice >= entries[i][0] && lookupPrice <= entries[i + 1][0]) {
          const t = (lookupPrice - entries[i][0]) / (entries[i + 1][0] - entries[i][0]);
          result[key] = entries[i][1] + t * (entries[i + 1][1] - entries[i][1]);
          break;
        }
      }
    }

    return { gex: result.gex, dex: result.dex, vex: result.vex, cex: result.cex, sessionDate: target.date };
  }

  setOptions(options) {
    this.options = { ...this.options, ...options };

    if (this.primitive) {
      this.clear();
      this.primitive = new GreeksHeatmapPrimitive(
        this.series,
        this.chart,
        this.options
      );
      this.series.attachPrimitive(this.primitive);
    }
  }

  /**
   * Toggle Hedging Demand (per-strike combined hedge flow profile bars, plus
   * the optional heatmap sub-layer). Lazily fetches its own /hedging-demand
   * payload on first enable (3.5 split), plus the exposure rows when the
   * realized mode needs the component recompute source.
   */
  async setShowHedgingDemand(show) {
    this.options.showHedgingDemand = show;
    if (this.enabled) {
      if (show && this.lastFetchParams) {
        const fetches = [];
        if (!this.hedgeRowsData) {
          fetches.push(this.fetchHedgingDemand(this.lastFetchParams.date, this.lastFetchParams.endDate));
        }
        if (this.options.hedgeDemandRealized && this._realizedHedgeSupported()
            && !this.exposureRowsData) {
          fetches.push(this.fetchExposureRows(this.lastFetchParams.date, this.lastFetchParams.endDate));
        }
        if (fetches.length > 0) await Promise.all(fetches);
      }
      this.draw();
    }
  }

  /**
   * Toggle the Hedging Demand heatmap mode (replaces the hedge profile bars
   * while on). Pure re-render: it reads the hedge rows already fetched
   * for the profile.
   */
  setShowHedgeDemandHeatmap(show) {
    this.options.showHedgeDemandHeatmap = !!show;
    if (this.enabled && this.options.showHedgingDemand) {
      this.draw();
    }
  }

  /**
   * Toggle T+1 forward OI bars visibility
   */
  setShowForwardOi(enabled) {
    this.options.showForwardOi = !!enabled;
    if (this.enabled) {
      this.draw();
    }
  }

  async setShowProjectedCurves(enabled) {
    this.options.showProjectedCurves = !!enabled;
    if (this.enabled) {
      if (enabled && !this.oiCurvesData && this.lastFetchParams) {
        await this.fetchOiCurves(this.lastFetchParams.date, this.lastFetchParams.endDate);
      }
      this.draw();
    }
  }

  setProjectedCurveGreeks(greeks) {
    this.options.projectedCurveGreeks = greeks;
    if (this.enabled && this.options.showProjectedCurves) {
      this.draw();
    }
  }

  setZeroLevelGreeks(greeks) {
    this.options.zeroLevelGreeks = greeks;
    if (this.enabled && this.options.showZeroLevels) {
      this.draw();
    }
  }

  /**
   * Toggle the Live Flips sub-layer (per-minute live-book crossings).
   * Lazily fetches the row history on first enable; the bake threshold
   * lines are unaffected either way.
   */
  async setShowZeroLevelsLive(enabled) {
    this.options.showZeroLevelsLive = !!enabled;
    if (this.enabled) {
      if (enabled && Object.keys(this._liveFlipsHistory).length === 0) {
        await this.fetchLiveCurves(this.lastFetchParams?.date || null);
      }
      this.drawSessionLevels();
    }
  }

  /**
   * Toggle the live Projected Curves sub-layer (today's curve follows the
   * live book; cursor scrubs stored 1-min states on historic candles).
   * Always re-fetches on enable so the scrub history loads for the whole
   * fetched range; off = bake curves, pixel-identical to before.
   */
  async setProjCurvesLive(enabled) {
    this.options.projCurvesLive = !!enabled;
    if (this.enabled) {
      if (enabled) {
        await this.fetchLiveCurves(this.lastFetchParams?.date || null);
      }
      this.draw();
    }
  }

  /**
   * Switch the Exposure Profile / Hedging Demand row source between the
   * frozen settlement smile and the per-minute refit (live) smile.
   * Refetches the rows from the selected family; frozen remains the
   * default and is unchanged when this stays off.
   */
  async setExpProfileLiveSmile(enabled) {
    const next = !!enabled;
    if (this.options.expProfileLiveSmile === next) return;
    this.options.expProfileLiveSmile = next;
    if (this.enabled && this.lastFetchParams) {
      const fetches = [];
      if (this.options.showExposureProfile
          || (this.options.showHedgingDemand && this.options.hedgeDemandRealized
              && this._realizedHedgeSupported())) {
        fetches.push(this.fetchExposureRows(this.lastFetchParams.date, this.lastFetchParams.endDate));
      }
      if (this.options.showHedgingDemand) {
        fetches.push(this.fetchHedgingDemand(this.lastFetchParams.date, this.lastFetchParams.endDate));
      }
      if (fetches.length > 0) {
        await Promise.all(fetches);
        this.draw();
      }
    }
  }

  /**
   * Toggle the Exposure Profile (per-strike exposure at current spot).
   * Lazily fetches the exposure rows on first enable.
   */
  async setShowExposureProfile(enabled) {
    this.options.showExposureProfile = !!enabled;
    if (this.enabled) {
      if (enabled && !this.exposureRowsData && this.lastFetchParams) {
        await this.fetchExposureRows(this.lastFetchParams.date, this.lastFetchParams.endDate);
      }
      this.draw();
    }
  }

  /**
   * Change the Exposure Profile weighting ('dealer' | 'absolute'). Pure
   * re-render: both weightings ride in every row payload.
   */
  setExposureProfileMode(mode) {
    this.options.exposureProfileMode = mode === 'absolute' ? 'absolute' : 'dealer';
    if (this.enabled && this.options.showExposureProfile) {
      this.draw();
    }
  }

  setExpProfileToggles(toggles) {
    this.options.expProfileToggles = toggles;
    if (this.enabled && this.options.showExposureProfile) {
      this.draw();
    }
  }

  /**
   * Toggle the Exposure Profile heatmap mode (replaces the profile bars
   * while on). Pure re-render: it reads the exposure rows already fetched
   * for the profile.
   */
  setShowExpProfileHeatmap(show) {
    this.options.showExpProfileHeatmap = !!show;
    if (this.enabled && this.options.showExposureProfile) {
      this.draw();
    }
  }

  /**
   * Toggle the T+1 Exposure Profile sub-layer (next session's book, live
   * per-minute series, column pinned at the right edge). Pure re-render:
   * the t1_rows series rides in every /exposure-rows response and the
   * forming row keeps arriving over SSE (ingest is gated on this flag,
   * so after re-enabling the next 5s tick refreshes the live edge).
   */
  setShowExpProfileT1(show) {
    this.options.showExpProfileT1 = !!show;
    if (this.enabled && this.options.showExposureProfile) {
      this.draw();
    }
  }

  /**
   * Toggle the Hedging Demand realized mode (actual trailing dS/dIV per
   * displayed window instead of the per-unit convention). The component
   * recompute reads the matching exposure candles (3.5 split), so
   * enabling it lazily fetches the exposure rows when absent; the ATM IV
   * series is fed by the chart component via setQuoteIvSegments.
   */
  async setHedgeDemandRealized(show) {
    this.options.hedgeDemandRealized = !!show;
    if (this.enabled && this.options.showHedgingDemand) {
      if (show && this._realizedHedgeSupported() && !this.exposureRowsData
          && this.lastFetchParams) {
        await this.fetchExposureRows(this.lastFetchParams.date, this.lastFetchParams.endDate);
      }
      this.draw();
    }
  }

  /**
   * Feed the quote-IV series (QuoteIVIndicator's per-day segments) for the
   * realized Hedging Demand mode: flattened to one time-sorted array for
   * the renderer's at-or-before lookups.
   */
  setQuoteIvSegments(segments) {
    if (!segments || !segments.length) {
      this._quoteIvFlat = null;
    } else {
      const flat = [];
      for (const seg of segments) {
        for (const p of (seg || [])) {
          if (p && p.time != null && typeof p.value === 'number') flat.push({ time: p.time, value: p.value });
        }
      }
      flat.sort((a, b) => a.time - b.time);
      this._quoteIvFlat = flat.length ? flat : null;
    }
    if (this.enabled && this.options.showHedgingDemand && this.options.hedgeDemandRealized) {
      this.draw();
    }
  }

  /**
   * Toggle the IV Skew (per-strike call/put IV dots, selected expiry
   * mode's book). Lazily fetches the smile rows on first enable.
   */
  async setShowIvSkew(enabled) {
    this.options.showIvSkew = !!enabled;
    if (this.enabled) {
      if (enabled && !this.ivSkewData && this.lastFetchParams) {
        await this.fetchIvSkew(this.lastFetchParams.date, this.lastFetchParams.endDate);
      }
      this.draw();
    }
  }

  /**
   * Toggle the Options Volume histogram (per-strike traded volume of the
   * forming/hovered candle). Lazily fetches the volume rows on first
   * enable.
   */
  async setShowOptionsVolume(enabled) {
    this.options.showOptionsVolume = !!enabled;
    if (this.enabled) {
      if (enabled && !this.volumeRowsData && this.lastFetchParams) {
        await this.fetchVolumeRows(this.lastFetchParams.date, this.lastFetchParams.endDate);
      }
      this.draw();
    }
  }

  setOptVolToggles(toggles) {
    this.options.optVolToggles = toggles;
    if (this.enabled && this.options.showOptionsVolume) {
      this.draw();
    }
  }

  /**
   * Toggle the Cumulative sub-histogram (day-to-date running totals on
   * their own "CV" axis). Pure re-render: the cumulative data IS the rows.
   */
  setOptVolCumulative(show) {
    this.options.optVolCumulative = !!show;
    if (this.enabled && this.options.showOptionsVolume) {
      this.draw();
    }
  }

  /**
   * Toggle the Buy/Sell split mode (paired dealer-buy/dealer-sell rows
   * instead of the totals row; dealer = negated aggressor). Pure
   * re-render: the split fields ride in every v2 row; pre-v2 rows fall
   * back to totals inside _drawVolumeBars.
   */
  setOptVolSplit(show) {
    this.options.optVolSplit = !!show;
    if (this.enabled && this.options.showOptionsVolume) {
      this.draw();
    }
  }

  /**
   * Toggle the Flow net view (one signed net-dealer-flow residual bar per
   * side, at-bid minus at-ask, on the residuals' own scale; precedence
   * over the Buy/Sell split view). Pure re-render over the v2 fields.
   */
  setOptVolFlowNet(show) {
    this.options.optVolFlowNet = !!show;
    if (this.enabled && this.options.showOptionsVolume) {
      this.draw();
    }
  }

  /**
   * Toggle the Shape History pane's data feed (parent-gated: the IV
   * Skew layer owns the smile fetch). On toggle-on, re-emits so the
   * pane repopulates from the already-fetched rows.
   */
  setIvSkewShapePane(enabled) {
    this.options.ivSkewShapePane = !!enabled;
    if (this.enabled && enabled) this._emitIvSkewShape();
  }

  /**
   * Toggle the Flow pane's data feed (parent-gated: Options Volume owns
   * the volume-rows fetch). On toggle-on, re-emits so the pane
   * repopulates from the already-fetched rows.
   */
  setVolFlowPane(enabled) {
    this.options.optVolFlowPane = !!enabled;
    if (this.enabled && enabled) this._emitVolFlow();
  }

  /**
   * Emit the per-window net dealer flow series (contracts) to the Flow
   * pane via options.onVolFlow: { [date]: [{time, callNet, putNet}] }.
   * Dealer-signed (at-bid minus at-ask, the histogram convention);
   * because the rows are day-to-date cumulative, each window's
   * strike-sum is the running net flow through that window. Rows without
   * the v2 split fields are skipped. No-op without a consumer; empty
   * emission clears the pane on a no-data context.
   */
  _emitVolFlow() {
    const cb = this.options.onVolFlow;
    if (typeof cb !== 'function' || !this.options.optVolFlowPane) return;
    const out = {};
    for (const dateKey of Object.keys(this.volumeRowsData || {})) {
      const rows = [];
      for (const candle of this.volumeRowsData[dateKey].candles || []) {
        const levels = candle.levels || [];
        if (candle.time == null || !levels.length || levels[0].call_vol_ask == null) continue;
        let callNet = 0;
        let putNet = 0;
        for (const lv of levels) {
          callNet += (lv.call_vol_bid || 0) - (lv.call_vol_ask || 0);
          putNet += (lv.put_vol_bid || 0) - (lv.put_vol_ask || 0);
        }
        rows.push({ time: candle.time, callNet, putNet });
      }
      if (rows.length) out[dateKey] = rows;
    }
    try {
      cb(out);
    } catch (e) {
      // Pane consumer errors must never break the plugin
    }
  }

  /**
   * Move a drag-to-place profile anchor. kind = 'today' (Exposure Profile
   * open-session axis) | 't1' (T+1 profile axis) | 'hedge' (Hedging Demand
   * open-session axis) | 'oi' (OI distribution developing-session axis)
   * | 'smile' (IV Skew open-session axis) | 'vol' (Options Volume
   * per-candle axis) | 'volcum' (Options Volume cumulative axis).
   * frac = axis X as a fraction of pane width, or null
   * to restore the default pin. Mutates the shared anchor ref the
   * renderer reads, so a lightweight requestUpdate repaints in place: called
   * at pointer rate during drags, so no full primitive rebuild.
   */
  setProfileAnchorFrac(kind, frac) {
    const value = frac == null ? null : frac;
    if (kind === 't1') {
      this._anchorRef.t1Frac = value;
      this.options.expProfileT1AnchorFrac = value;
    } else if (kind === 'hedge') {
      this._anchorRef.hedgeFrac = value;
      this.options.hedgeDemandAnchorFrac = value;
    } else if (kind === 'oi') {
      this._anchorRef.oiFrac = value;
      this.options.oiProfileAnchorFrac = value;
    } else if (kind === 'smile') {
      this._anchorRef.smileFrac = value;
      this.options.ivSkewAnchorFrac = value;
    } else if (kind === 'vol') {
      this._anchorRef.volFrac = value;
      this.options.optVolAnchorFrac = value;
    } else if (kind === 'volcum') {
      this._anchorRef.volCumFrac = value;
      this.options.optVolCumAnchorFrac = value;
    } else {
      this._anchorRef.todayFrac = value;
      this.options.expProfileAnchorFrac = value;
    }
    if (this.enabled && this.primitive) {
      this.primitive.requestUpdate();
    }
  }

  /**
   * Last-drawn draggable axis geometry ({today, t1, hedge} of {x, minY, maxY} in
   * media px, null when not drawn), written by the renderer each paint.
   * Read by the chart component to hit-test pointer events for drags.
   */
  getAnchorHit() {
    return this._anchorHitRef;
  }

  setOiDistToggles(toggles) {
    this.options.oiDistToggles = toggles;
    if (this.enabled && this.options.showOiDistribution) {
      this.draw();
    }
  }

  /**
   * Toggle the Open Interest distribution and re-fetch data only once.
   * @param {object} settings - { showOiDistribution }
   * @param {string} date - Current date to fetch data for
   */
  async setOiSettings({ showOiDistribution }, date) {
    const changed = this.options.showOiDistribution !== showOiDistribution;

    this.options.showOiDistribution = showOiDistribution;

    if (this.enabled && changed) {
      console.log('[GreeksHeatmap] OI settings changed - Distribution:', showOiDistribution);
      if (showOiDistribution && !this.oiBarsData) {
        const today = new Date().toISOString().split('T')[0];
        await this.fetchOiBars(date, today);
      } else if (!showOiDistribution) {
        this.oiBarsData = null;
      }
      this.draw();
    }
  }

  // =========================================================================
  // SESSION LEVEL INDICATORS (PDL, OR, IB)
  // =========================================================================

  /**
   * Set session level data from useLevelIndicators hook
   */
  setLevelData(levelData) {
    this.sessionLevelData = levelData;
    this.drawSessionLevels();
  }

  /**
   * Toggle Previous Day Levels visibility
   */
  setShowPrevDayLevels(show) {
    this.options.showPrevDayLevels = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle Prior Week Levels visibility (PWH/PWL/PWC/PWO)
   */
  setShowPriorWeekLevels(show) {
    this.options.showPriorWeekLevels = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle Prior Month Levels visibility (PMH/PML/PMC/PMO)
   */
  setShowPriorMonthLevels(show) {
    this.options.showPriorMonthLevels = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle Asia Session High/Low visibility (ASH/ASL)
   */
  setShowAsiaLevels(show) {
    this.options.showAsiaLevels = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle London Session High/Low visibility (LDH/LDL)
   */
  setShowLondonLevels(show) {
    this.options.showLondonLevels = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle Opening Range visibility
   */
  setShowOpeningRange(show) {
    this.options.showOpeningRange = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle Initial Balance visibility
   */
  setShowInitialBalance(show) {
    this.options.showInitialBalance = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle Opening Prints visibility (GOP/RTHOP)
   */
  setShowOpeningPrints(show) {
    this.options.showOpeningPrints = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle Pivot Points visibility
   */
  setShowPivotPoints(show) {
    this.options.showPivotPoints = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle Zero Levels visibility (zero-crossing key levels). Levels are static
   * per session (served by /zero-levels); no curve payload needed.
   */
  setShowZeroLevels(show) {
    this.options.showZeroLevels = show;
    this.drawSessionLevels();
  }

  /**
   * Toggle Gamma Levels visibility (at-spot GVWAP, band, walls). Static
   * per session (served by /gamma-levels).
   */
  setShowGammaLevels(show) {
    this.options.showGammaLevels = show;
    this.drawSessionLevels();
  }

  /**
   * Set Gamma Levels sub-toggle visibility ({gvwap, band, walls}).
   * Repaints via drawSessionLevels: the gamma block renders in the session
   * level primitive, which draw() never rebuilds.
   */
  setGammaLevelToggles(toggles) {
    this.options.gammaLevelToggles = toggles || { gvwap: true, band: true, walls: true };
    this.drawSessionLevels();
  }

  /**
   * Toggle Expected Move visibility (IV-based EM bands)
   */
  setShowExpectedMove(show) {
    this.options.showExpectedMove = show;
    this.drawSessionLevels();
  }

  /**
   * Draw session level indicators independently of GEX data
   * This allows PDL/OR/IB to render without needing GEX heatmap enabled
   */
  drawSessionLevels() {
    if (!this.series || !this.chart) {
      console.warn('[GreeksHeatmap] Cannot draw session levels - no chart/series');
      return;
    }

    // Check if chart has data (required for price scale operations)
    try {
      const timeScale = this.chart.timeScale();
      if (!timeScale || !timeScale.getVisibleLogicalRange()) {
        // Chart not ready yet - will be called again when data is loaded
        return;
      }
    } catch (e) {
      // Chart in invalid state
      return;
    }

    // Zero Levels scrub with the crosshair but may be the only indicator on
    // (their toggle path never reaches draw()), so register the shared
    // crosshair handler here too.
    this._ensureCrosshairSubscription();

    // Check if any session level indicator is enabled
    const anySessionLevelEnabled =
      this.options.showPrevDayLevels ||
      this.options.showPriorWeekLevels ||
      this.options.showPriorMonthLevels ||
      this.options.showAsiaLevels ||
      this.options.showLondonLevels ||
      this.options.showOpeningRange ||
      this.options.showInitialBalance ||
      this.options.showOpeningPrints ||
      this.options.showPivotPoints ||
      this.options.showZeroLevels ||
      this.options.showGammaLevels ||
      this.options.showExpectedMove;

    // Remove existing session level primitive
    if (this.sessionLevelPrimitive) {
      try {
        this.series.detachPrimitive(this.sessionLevelPrimitive);
      } catch (e) {
        // Ignore detach errors
      }
      this.sessionLevelPrimitive = null;
    }

    // If no session levels enabled or no data, we're done
    if (!anySessionLevelEnabled || !this.sessionLevelData) {
      return;
    }

    // Check if we have any actual level data (per-day map)
    const hasMultiDayData =
      this.sessionLevelData.levelsByDay &&
      Object.keys(this.sessionLevelData.levelsByDay).length > 0;

    if (!hasMultiDayData) {
      console.log('[GreeksHeatmap] No session level data to draw');
      return;
    }

    // Create a simple primitive for session levels (all session-static;
    // Zero Levels included, so no crosshair or curve accessors needed).
    this.sessionLevelPrimitive = new SessionLevelPrimitive(
      this.series,
      this.chart,
      this.sessionLevelData,
      // Stable refs ride the options snapshot: liveFlipsHistory is
      // mutated in place (SSE appends) and crosshairRef per mouse move,
      // so hover scrubbing repaints via requestUpdate without a
      // primitive rebuild; new-minute data still rebuilds via
      // drawSessionLevels().
      { ...this.options, liveFlipsHistory: this._liveFlipsHistory,
        crosshairRef: this._crosshairRef }
    );

    try {
      this.series.attachPrimitive(this.sessionLevelPrimitive);
    } catch (e) {
      console.error('[GreeksHeatmap] Error attaching session level primitive:', e);
    }
  }
}

export default GreeksHeatmapPlugin;
